import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BOOTH_RULES_CONFIG } from '../services/boothRulesService';
import { CUSTOM_BOOTH_RULES } from '../config/customBoothRules';
import RulesAdminPanel from './RulesAdminPanel';
import CellTooltip from './CellTooltip.jsx';
import { getLayoutDefaults } from '../services/defaultsService';
import {
  getUserLayouts,
  deleteLayout as deleteLayoutFromDB,
  listAllLayouts,
  createLayout as createLayoutInDB,
  updateLayout as updateLayoutInDB,
  updateLayoutStatus as updateLayoutStatusInDB
} from '../services/layoutsService';
import { listProfilesByType } from '../services/profilesService';
import { updateLayoutCellGroupIds, updateLayoutCellFields } from '../services/layoutsService';

// Level to color mapping - now uses custom level definitions
const getLevelColor = (level, customLevels) => {
  // Return configured color when present, otherwise a safe default.
  return customLevels?.[level]?.color || '#95a5a6';
};

// Small Error Boundary to catch render/runtime errors inside LayoutBuilder
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log to console; in future could report to monitoring service
    console.error('LayoutBuilder caught error:', error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary" style={{ padding: 20 }}>
          <h3>Something went wrong rendering the Layout Builder</h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{String(this.state.error)}</p>
          {this.state.errorInfo && (
            <details style={{ whiteSpace: 'pre-wrap' }}>
              {this.state.errorInfo.componentStack}
            </details>
          )}
          <div style={{ marginTop: 12 }}>
            <button onClick={this.reset} style={{ marginRight: 8 }}>Try again</button>
            <button onClick={() => window.location.reload()}>Reload page</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Get level pricing
const getLevelPricing = (level, isCorner, customLevels) => {
  const levelData = customLevels?.[level];
  if (!levelData) return undefined;
  return isCorner ? levelData.cornerPrice : levelData.inlinePrice;
};

// Helper: format cents (integer) into localized USD currency string
const formatCentsToUSD = (cents) => {
  if (cents === null || typeof cents === 'undefined') return 'N/A';
  if (Number(cents) === 0) return 'N/A';
  try {
    const dollars = Number(cents) / 100;
    return dollars.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  } catch {
    return `$${cents}`;
  }
};

// Helper function to get the effective corner value considering manual overrides
const getEffectiveCornerValue = (cell) => {
  if (cell.cornerOverride) {
    return cell.manualCorner || cell.corner;
  }
  return cell.corner;
};

// Get level name
const getLevelName = (level, customLevels) => {
  return customLevels?.[level]?.name || level;
};

// Generate basic cell properties
/**
 * Generate booth number in alpha-numeric format (A01, A02, B01, etc.)
 * For rows > 26, uses two letters: AA01, AB01, etc.
 * @param {number} row - Row number (1-based)
 * @param {number} col - Column number (1-based)
 * @returns {string} Booth number in format like "A01", "B15", "AA01", "AB02", etc.
 */
const generateBoothNumber = (row, col) => {
  // Convert row number to letter sequence (1=A, 2=B, ..., 26=Z, 27=AA, 28=AB, etc.)
  let rowLetter = '';
  let rowNum = row - 1; // Convert to 0-based for easier calculation
  
  if (rowNum < 26) {
    // Single letter: A-Z
    rowLetter = String.fromCharCode(65 + rowNum);
  } else {
    // Multiple letters: AA, AB, AC, ..., BA, BB, etc.
    const firstLetter = Math.floor(rowNum / 26) - 1;
    const secondLetter = rowNum % 26;
    rowLetter = String.fromCharCode(65 + firstLetter) + String.fromCharCode(65 + secondLetter);
  }
  
  // Format column as zero-padded two digits
  const colNumber = col.toString().padStart(2, '0');
  
  return `${rowLetter}${colNumber}`;
};

/**
 * Apply numbering to grid data using specified scheme
 * @param {Array} gridData - Array of cell objects
 * @param {string} scheme - Numbering scheme ID
 * @param {boolean} skipEmptyRows - Whether to skip rows with all invisible cells (only applies to 4-digit and alpha-numeric)
 * @param {number} totalRows - Total number of rows in grid
 * @param {number} totalColumns - Total number of columns in grid
 * @returns {Array} Updated grid data with proper booth numbers
 */
const applyBoothNumbering = (gridData, scheme = 'alpha-numeric', skipEmptyRows = false, totalRows = 0, totalColumns = 0) => {
  // Create mapping for non-empty rows (only for 4-digit and alpha-numeric schemes)
  let rowMapping = {};
  
  // For the alternating-rows scheme we always ignore empty rows per requirement
  const effectiveSkipEmptyRows = skipEmptyRows || (scheme === 'four-digit-alternating-rows');

  if (effectiveSkipEmptyRows && (scheme === 'four-digit-numbers' || scheme === 'alpha-numeric' || scheme === 'four-digit-alternating-rows')) {
    // Find rows that contain at least one visible cell
    const visibleRows = new Set();
    
    gridData.forEach(cell => {
      if (cell.status !== 'Invisible') {
        visibleRows.add(cell.row);
      }
    });
    
    // Create mapping for rows. Numbering should start from the bottom row,
    // so sort visible rows descending (bottom -> top) and assign 1..N
    const sortedVisibleRows = Array.from(visibleRows).sort((a, b) => b - a);
    sortedVisibleRows.forEach((originalRow, index) => {
      rowMapping[originalRow] = index + 1;
    });
  } else {
    // Map rows so numbering starts from the bottom row.
    // For a physical row i (1..totalRows) the mapped numbering should be (totalRows - i + 1).
    for (let i = 1; i <= totalRows; i++) {
      rowMapping[i] = (totalRows - i + 1);
    }
  }

  // Determine mapped total rows (after skip-empty-rows remapping) for schemes that care about mapped row counts
  let mappedTotalRows = totalRows;
  if (effectiveSkipEmptyRows && (scheme === 'four-digit-numbers' || scheme === 'alpha-numeric' || scheme === 'four-digit-alternating-rows')) {
    mappedTotalRows = Object.keys(rowMapping).length;
  }

  // Create final mapped row objects for all cells
  const cellMappings = new Map();
  gridData.forEach(cell => {
    // Use the rowMapping we constructed for both skip and non-skip cases.
    // When a row is intentionally skipped (not present in mapping), mappedRow will be null.
    const mappedRow = rowMapping[cell.row] !== undefined ? rowMapping[cell.row] : null;
    const mappedCol = cell.col; // Always use original column
    cellMappings.set(cell.id, { mappedRow, mappedCol });
  });

    // Helper: compute paired prefix and alternate column number used by both
    // four-digit-alternating and alpha-numeric alternating schemes
    const computeAlternatingPrefixAndAltCol = (mappedRow, mappedCol) => {
      // Pair mapped rows into the same prefix: rows 1+2 -> prefix 1, 3+4 -> prefix 2, etc.
      const prefix = Math.ceil(mappedRow / 2);
      // Determine whether this mapped row should use odd columns first.
      // Use mappedRow parity: mappedRow 1 (bottom) -> odd, 2 -> even, 3 -> odd, etc.
      const isOddColumns = (mappedRow % 2) === 1;
      const altCol = isOddColumns ? (mappedCol * 2 - 1) : (mappedCol * 2);
      return { prefix, altCol };
    };

  if (scheme === 'four-digit-numbers') {
    // Use mapped row and column numbers to create 4-digit numbers
    return gridData.map(cell => {
      if (cell.status === 'Invisible') {
        return { ...cell, number: null };
      }
      
      const mapping = cellMappings.get(cell.id);
      if (mapping.mappedRow === null || mapping.mappedCol === null) {
        return { ...cell, number: null }; // Cell in skipped row/column
      }
      // Preserve manual overrides
      if (cell.numberOverride || cell.manualNumber) {
        return { ...cell, number: cell.manualNumber || cell.number };
      }

      const fourDigitNumber = `${mapping.mappedRow.toString().padStart(2, '0')}${mapping.mappedCol.toString().padStart(2, '0')}`;
      return { ...cell, number: fourDigitNumber };
    });
  }

  if (scheme === 'four-digit-alternating-rows') {
    // 4-digit numbering that groups physical rows into paired prefixes.
    // Rows are treated in pairs: rows 1+2 => prefix 01, rows 3+4 => prefix 02, etc.
    // Within each pair the first physical row gets odd column numbers (01,03,05...),
    // and the second physical row gets even column numbers (02,04,06...).
    return gridData.map(cell => {
      if (cell.status === 'Invisible') {
        return { ...cell, number: null };
      }

      const mapping = cellMappings.get(cell.id);
      if (mapping.mappedRow === null || mapping.mappedCol === null) {
        return { ...cell, number: null };
      }

      // Preserve manual overrides
      if (cell.numberOverride || cell.manualNumber) {
        return { ...cell, number: cell.manualNumber || cell.number };
      }

      // Determine the paired prefix: ceil(mappedRow / 2)
        const { prefix, altCol } = computeAlternatingPrefixAndAltCol(mapping.mappedRow, mapping.mappedCol);
        const fourDigitNumber = `${prefix.toString().padStart(2, '0')}${altCol.toString().padStart(2, '0')}`;
        return { ...cell, number: fourDigitNumber };
    });
  }
  
  if (scheme === 'sequential-numbers') {
    // Sequential numbering - skip parameters don't apply to this scheme
    // First pass: identify all visible cells to be numbered
    const cellsToNumber = [];

    gridData.forEach(cell => {
      if (cell.status === 'Invisible') {
        return; // Skip invisible cells
      }

      // For sequential numbering, include all visible cells regardless of skip parameters
      cellsToNumber.push(cell);
    });

    // Sort so numbering starts from the bottom mapped row (mappedRow 1 = bottom),
    // then left-to-right by column. Use the mappedRow from cellMappings.
    cellsToNumber.sort((a, b) => {
      const aMap = cellMappings.get(a.id) || { mappedRow: Number.POSITIVE_INFINITY };
      const bMap = cellMappings.get(b.id) || { mappedRow: Number.POSITIVE_INFINITY };
      const aRow = aMap.mappedRow === null ? Number.POSITIVE_INFINITY : aMap.mappedRow;
      const bRow = bMap.mappedRow === null ? Number.POSITIVE_INFINITY : bMap.mappedRow;
      if (aRow !== bRow) return aRow - bRow;
      return a.col - b.col;
    });
    
    // Second pass: assign sequential numbers to qualifying cells
    const numberedCells = new Map();
    cellsToNumber.forEach((cell, index) => {
      const number = (index + 1).toString().padStart(3, '0');
      numberedCells.set(cell.id, number);
    });
    
    // Third pass: apply numbers to all cells
    return gridData.map(cell => {
      if (cell.status === 'Invisible') {
        return { ...cell, number: null };
      }

      // Preserve manual overrides
      if (cell.numberOverride || cell.manualNumber) {
        return { ...cell, number: cell.manualNumber || cell.number };
      }

      const assignedNumber = numberedCells.get(cell.id);
      return { ...cell, number: assignedNumber || null };
    });
  }
  
  // Alpha-numeric scheme (with alternating-row display pairing)
  if (scheme === 'alpha-numeric') {
    return gridData.map(cell => {
      if (cell.status === 'Invisible') {
        return { ...cell, number: null };
      }

      // Preserve manual overrides
      if (cell.numberOverride || cell.manualNumber) {
        return { ...cell, number: cell.manualNumber || cell.number };
      }

      const mapping = cellMappings.get(cell.id);
      if (mapping.mappedRow === null || mapping.mappedCol === null) {
        return { ...cell, number: null }; // Cell in skipped row/column
      }

      const { prefix, altCol } = computeAlternatingPrefixAndAltCol(mapping.mappedRow, mapping.mappedCol);
      const boothNumber = generateBoothNumber(prefix, altCol);
      return { ...cell, number: boothNumber };
    });
  }

  // Fallback: leave numbers unchanged (shouldn't reach here normally)
  return gridData.map(cell => ({ ...cell }));
};

const generateCellData = (row, col, columns) => {
  // Generate booth number using new alpha-numeric format
  const boothNumber = generateBoothNumber(row, col);
  // Generate 4-digit ID: first two digits = row, last two digits = column
  const cellId = `${row.toString().padStart(2, '0')}${col.toString().padStart(2, '0')}`;

  return {
    id: cellId,
    text: cellId,
    number: boothNumber,
    // Default visible cell values for a new layout
    status: 'Available',
    level: 'Basic',
    corner: 'N',
    row: row,
    col: col,
    created: new Date().toLocaleTimeString()
  };
};

  // Helper: build rulesConfig object from a saved layout document
  // Apply any defaults found in the app defaults document to the current rules config.
  // This will look for common keys where rule defaults may be stored and merge numeric
  // parameters and priority values into the in-memory rulesConfig used by the UI.
  function applyDefaultsToRules(config = {}, defs = {}) {
    if (!defs) return config;
    // Candidate locations for rule defaults in the defaults document
    const candidates = defs.rules || defs.rulesDefaults || defs.boothRules || defs.boothRulesConfig || {};

    const out = { ...config };
    Object.entries(out).forEach(([key, rule]) => {
      if (!rule) return;
      // Look up overrides by rule key or by rule id
      const overrides = (candidates && (candidates[key] || candidates[rule.id])) || null;
      if (!overrides) return;
      // If overrides contains a `parameters` object, merge that first
      if (overrides.parameters && typeof overrides.parameters === 'object') {
        out[key] = { ...out[key], parameters: { ...out[key].parameters, ...overrides.parameters } };
      }
      // Merge top-level numeric/string/boolean fields (including priority)
      Object.entries(overrides).forEach(([k, v]) => {
        if (k === 'parameters') return;
        if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
          out[key] = { ...out[key], [k]: v };
        }
      });
    });
    return out;
  }
  // Accept optional defaults document `defs` and apply defaults first,
  // then overlay values from the saved layout so layout values take precedence.
  function buildRulesConfigFromLayout(layout, defs = null) {
    if (!layout) return { ...BOOTH_RULES_CONFIG };
    let base = { ...BOOTH_RULES_CONFIG };
    // If defaults provided, merge them into the base so they act as defaults
    if (defs) {
      try {
        base = applyDefaultsToRules(base, defs);
      } catch (e) {
        console.warn('Failed to apply rule defaults to base config:', e);
      }
    }
    // Build a fresh config with all disabled, no groups
    const config = Object.keys(base).reduce((acc, key) => {
      acc[key] = { ...base[key], enabled: false, groups: undefined };
      return acc;
    }, {});

    const resolveRuleKey = (idOrKey) => {
      if (!idOrKey) return undefined;
      if (config[idOrKey]) return idOrKey;
      const match = Object.entries(config).find(([, rule]) => rule.id === idOrKey);
      return match ? match[0] : undefined;
    };

    const dbRules = Array.isArray(layout.rules) ? layout.rules : [];

    // Load rule enablement + parameters
    dbRules.forEach(({ ruleId, parameters }) => {
      const key = resolveRuleKey(ruleId);
      if (!key || !config[key]) return;
      config[key].enabled = true;
      config[key].parameters = parameters;
      // Merge saved simple parameters back onto top-level for UI editing
      if (parameters && typeof parameters === 'object') {
        Object.entries(parameters).forEach(([k, v]) => {
          if (k === 'groups') return;
          if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
            config[key][k] = v;
          }
        });
      }
    });

    // Preferred: load booth groups from top-level field
    const dbBoothGroups = layout.boothGroups || null;
    if (dbBoothGroups && typeof dbBoothGroups === 'object') {
      Object.entries(dbBoothGroups).forEach(([ruleKeyOrId, groups]) => {
        const key = resolveRuleKey(ruleKeyOrId);
        if (!key || !config[key]) return;
        config[key].groups = Array.isArray(groups) ? groups : undefined;
      });
    } else {
      // Legacy fallback: groups stored inside rules entries
      dbRules.forEach(({ ruleId, groups }) => {
        const key = resolveRuleKey(ruleId);
        if (key && Array.isArray(groups)) {
          config[key].groups = groups;
        }
      });
    }

    return config;
  }

const GridCell = ({ cellData, onClick, isSelected, isRequired, isBoothMode, cellSize, fontSize, isMultiSelect, customLevels, rulesConfig = {} }) => {

  // Generate a deterministic pastel color from a string (used for group badges)
  const hashStringToColor = (str) => {
    if (!str) return '#9aa0a6';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash; // Convert to 32bit int
    }
    const h = Math.abs(hash) % 360; // hue
    const s = 60 + (Math.abs(hash) % 20); // saturation 60-79
    const l = 50; // lightness
    return `hsl(${h} ${s}% ${l}%)`;
  };

  // Get color from level definition
  const cellColor = getLevelColor(cellData.level, customLevels);

  // Determine text color based on background brightness
  const getTextColor = (backgroundColor) => {
    if (!backgroundColor) return '#333333';
    // Handle hex colors like #rrggbb or #rgb
    if (typeof backgroundColor === 'string' && backgroundColor.startsWith('#')) {
      let hex = backgroundColor.replace('#', '');
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
      }
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128 ? '#333333' : '#ffffff';
    }
    // Handle hsl(...) strings by parsing lightness as a heuristic
    if (typeof backgroundColor === 'string' && backgroundColor.toLowerCase().startsWith('hsl')) {
      try {
        const m = backgroundColor.match(/\b(\d{1,3})%\s*\)?$/);
        if (m && m[1]) {
          const lightness = Number(m[1]);
          return lightness > 50 ? '#333333' : '#ffffff';
        }
      } catch (e) {}
      return '#ffffff';
    }
    // Fallback to dark text for unknown formats
    return '#333333';
  };

  // Determine border styling based on mode and state
  const getBorderColor = () => {
    if (isSelected) return '#ffff00'; // Yellow for selected
    if (isRequired) return '#ff4444'; // Red for required
    if (cellData.corner === 'Y') return '#000000'; // Black for corner
    return 'transparent';
  };

  const getBorderWidth = () => {
    if (isRequired) return '4px'; // Thicker border for required
    if (cellData.corner === 'Y') return '3px';
    return '2px';
  };

  // Determine cursor and opacity based on booth mode
  const getCellStyle = () => {
    const baseStyle = {
      backgroundColor: cellColor,
      borderColor: getBorderColor(),
      borderWidth: getBorderWidth(),
      width: `${cellSize}px`,
      height: `${cellSize}px`,
      color: getTextColor(cellColor)
    };

    if (isBoothMode) {
      if (cellData.status !== 'Available') {
        // Unavailable booths in booth mode
        baseStyle.opacity = '0.4';
        baseStyle.cursor = 'not-allowed';
      } else {
        baseStyle.cursor = 'pointer';
      }
    }

    return baseStyle;
  };

  // Tooltip state and handlers (small tooltip showing booth number and group)
  const btnRef = useRef(null);
  const [ttVisible, setTtVisible] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const handleMouseEnter = () => {
    const r = btnRef.current && btnRef.current.getBoundingClientRect();
    if (r) setAnchorRect(r);
    setTtVisible(true);
  };
  const handleMouseMove = () => {
    const r = btnRef.current && btnRef.current.getBoundingClientRect();
    if (r) setAnchorRect(r);
  };
  const handleMouseLeave = () => setTtVisible(false);
  const handleFocus = () => {
    const r = btnRef.current && btnRef.current.getBoundingClientRect();
    if (r) setAnchorRect(r);
    setTtVisible(true);
  };
  const handleBlur = () => setTtVisible(false);

  // Resolve group name from provided rulesConfig
  const resolveGroupName = () => {
    if (!cellData || !cellData.groupid) return 'No';
    try {
      const gid = String(cellData.groupid);
      const rules = Object.values(rulesConfig || {});
      for (const r of rules) {
        if (r && Array.isArray(r.groups)) {
          const g = r.groups.find(grp => String(grp.id) === gid);
          if (g) return g.name || 'Unnamed Group';
        }
      }
    } catch (e) {}
    return 'No';
  };
  const groupName = resolveGroupName();
  // Other attributes for tooltip (mirrors BoothSelection tooltip)
  const statusMessage = cellData && cellData.status ? cellData.status : 'Available';
  const isInvisible = cellData && cellData.status === 'Invisible';
  const levelName = cellData && cellData.level ? getLevelName(cellData.level, customLevels) : 'Unknown';
  const isCornerCell = getEffectiveCornerValue(cellData) === 'Y';
  const priceVal = getLevelPricing(cellData.level, isCornerCell, customLevels);
  const priceLabel = formatCentsToUSD(priceVal);

  return (
    <button
      className={`grid-cell 
        ${isSelected ? 'selected' : ''} 
        ${isMultiSelect ? 'multi-select-mode' : ''} 
        ${cellData.corner === 'Y' ? 'corner-cell' : ''} 
        ${isBoothMode ? 'booth-mode' : ''}
        ${isRequired ? 'required-booth' : ''}
        ${isBoothMode && cellData.status !== 'Available' ? 'unavailable-booth' : ''}
      `}
      ref={btnRef}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={(e) => onClick(cellData, e)}
      style={getCellStyle()}
      disabled={isBoothMode && cellData.status !== 'Available'}
    >
      <CellTooltip visible={ttVisible && !isInvisible} anchorRect={anchorRect}>
        <span className="tt-line"><strong>Booth:</strong> {cellData.number}</span>
        <span className="tt-line"><strong>Status:</strong> {statusMessage}</span>
        <span className="tt-line"><strong>Level:</strong> {levelName}</span>
        <span className="tt-line"><strong>Corner:</strong> {isCornerCell ? 'Yes' : 'No'}</span>
        <span className="tt-line"><strong>Price:</strong> {priceLabel}</span>
        <span className="tt-line"><strong>Group:</strong> {groupName === 'No' ? 'No' : groupName}</span>
      </CellTooltip>
      <div className="cell-content" style={{ position: 'relative' }}>
        <div 
          className="cell-number" 
          style={{ fontSize: `${fontSize}px` }}
        >
          {cellData.numberOverride || cellData.manualNumber ? (cellData.manualNumber || cellData.number) : cellData.number}
        </div>
        {/* Group badge: small dot indicating this cell belongs to a group */}
        {cellData && cellData.groupid && (
          <div
            className="group-badge"
            title={`Group: ${cellData.groupid}`}
            style={{
              position: 'absolute',
              left: '4px',
              bottom: '4px',
              width: Math.max(8, Math.floor(cellSize * 0.18)) + 'px',
              height: Math.max(8, Math.floor(cellSize * 0.18)) + 'px',
              borderRadius: '50%',
              backgroundColor: hashStringToColor(String(cellData.groupid)),
              border: '1px solid rgba(0,0,0,0.15)',
              boxShadow: '0 0 0 2px rgba(255,255,255,0.15) inset'
            }}
          />
        )}
        {isSelected && isMultiSelect && (
          <div className="selection-indicator">✓</div>
        )}
        {isRequired && (
          <div className="required-indicator">!</div>
        )}
        {isBoothMode && cellData.status !== 'Available' && (
          <div className="unavailable-indicator">✖</div>
        )}
      </div>
    </button>
  );
};

const PropertyPopup = ({ selectedCell, onClose, show, onApplyChanges, customLevels, rulesConfig = {} }) => {
  const [editLevel, setEditLevel] = useState('');
  const [showInvisibleMessage, setShowInvisibleMessage] = useState(false);
  // cornerMode: 'Auto' | 'Inline' | 'Corner' (UI-level representation)
  const [cornerMode, setCornerMode] = useState('Auto');
  const [numberOverride, setNumberOverride] = useState(false);
  const [manualNumber, setManualNumber] = useState('');
  
  const levels = Object.keys(customLevels || {}).filter(level => level !== 'Invisible'); // Filter out 'Invisible'

  // Reset form when cell changes
  React.useEffect(() => {
    if (selectedCell) {
      setEditLevel(selectedCell.level);
      // Initialize cornerMode from stored flags
      // Prefer explicit stored manualCorner if present (handles legacy/partial writes)
      if (selectedCell.manualCorner === 'Y' || selectedCell.manualCorner === 'N') {
        setCornerMode(selectedCell.manualCorner === 'Y' ? 'Corner' : 'Inline');
      } else if (selectedCell.cornerOverride) {
        const m = selectedCell.manualCorner || selectedCell.corner;
        setCornerMode(m === 'Y' ? 'Corner' : 'Inline');
      } else {
        setCornerMode('Auto');
      }
      setNumberOverride(selectedCell.numberOverride || false);
      setManualNumber(selectedCell.manualNumber || selectedCell.number || '');
    }
  }, [selectedCell]);

  // Resolve group name from rulesConfig based on selectedCell.groupid
  const resolveGroupName = () => {
    if (!selectedCell || !selectedCell.groupid) return 'No';
    try {
      const gid = String(selectedCell.groupid);
      const rules = Object.values(rulesConfig || {});
      for (const r of rules) {
        if (r && Array.isArray(r.groups)) {
          const g = r.groups.find(grp => String(grp.id) === gid);
          if (g) return g.name || 'Unnamed Group';
        }
      }
    } catch (e) {}
    return 'No';
  };
  const groupName = resolveGroupName();

  // NOTE: `storedCornerOverride` belongs to the single-cell property popup
  // (uses `selectedCell`). GridCell should not reference `selectedCell`.
  const storedCornerOverride = !!(selectedCell && (selectedCell.cornerOverride === true || selectedCell.manualCorner === 'Y' || selectedCell.manualCorner === 'N'));

  // Handle case when user tries to edit invisible cells
  React.useEffect(() => {
    if (selectedCell && (selectedCell.status === 'Invisible' || selectedCell.level === 'Invisible')) {
      setShowInvisibleMessage(true);
    } else {
      setShowInvisibleMessage(false);
    }
  }, [selectedCell]);

  if (!show || !selectedCell) return null;

  const handleApply = () => {
    const changes = {};
    // Handle level changes
    if (editLevel && editLevel !== selectedCell.level) {
      changes.level = editLevel;
    }
    
    // Handle corner override changes via cornerMode dropdown
    if (cornerMode === 'Auto') {
      if (selectedCell.cornerOverride) {
        changes.cornerOverride = false;
        changes.manualCorner = undefined;
      }
    } else {
      const desired = cornerMode === 'Corner' ? 'Y' : 'N';
      if (!selectedCell.cornerOverride || selectedCell.manualCorner !== desired) {
        changes.cornerOverride = true;
        changes.corner = desired;
        changes.manualCorner = desired;
      }
    }
    
    // Color is now derived from level - no need to set it
    // Handle number override
    if (numberOverride !== (selectedCell.numberOverride || false)) {
      changes.numberOverride = numberOverride;
    }

    if (numberOverride && (manualNumber !== (selectedCell.manualNumber || selectedCell.number))) {
      changes.number = manualNumber;
      changes.manualNumber = manualNumber;
    }

    if (!numberOverride && (selectedCell.numberOverride || selectedCell.manualNumber)) {
      // Clearing override
      changes.numberOverride = false;
      changes.manualNumber = undefined;
      // Recompute number will happen when numbers are reapplied elsewhere
    }
    
    if (Object.keys(changes).length > 0) {
      onApplyChanges(selectedCell.id, changes);
    }
    onClose();
  };

  const handleClearNumberOverride = () => {
    // Clear override locally
    setNumberOverride(false);
    setManualNumber('');
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content single-cell-editor compact-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header compact-header">
          <h4>Edit Properties</h4>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        {/* custom number section moved below corner override for better visual grouping */}
        
        <div className="popup-body compact">
          <div className="property-section compact-property-section">
            {/* Header with booth info */}
            <div className="booth-header">
              <div className="booth-title">
                <strong>#{selectedCell.number}</strong>
                <div className="corner-status-container">
                  {cornerMode !== 'Auto' ? (
                    // Manual override mode - show selected value prominently
                    <>
                      <span className={`corner-badge ${cornerMode === 'Corner' ? 'is-corner' : 'not-corner'}`}>
                        {cornerMode === 'Corner' ? 'CORNER' : 'INLINE'}
                      </span>
                      <span className={`corner-badge crossed-out ${selectedCell.corner === 'Y' ? 'is-corner' : 'not-corner'}`}>
                        {selectedCell.corner === 'Y' ? 'CORNER' : 'INLINE'}
                      </span>
                    </>
                  ) : (
                    // Auto mode - show calculated value
                    <span className={`corner-badge ${selectedCell.corner === 'Y' ? 'is-corner' : 'not-corner'}`}>
                      {selectedCell.corner === 'Y' ? 'CORNER' : 'INLINE'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Message for invisible cells */}
            {showInvisibleMessage && (
              <div className="invisible-message">
                <div className="message-content">
                  <strong>⚠️ This cell is invisible</strong>
                  <p>To make this cell visible or toggle its visibility, please use the "Toggle Invisible" button in the main toolbar instead of editing properties here.</p>
                </div>
              </div>
            )}

            {/* Compact controls */}
            <div className="compact-controls">
              {/* Status removed: all booths are Available at design time */}

              <div className="control-row">
                <label>Level:</label>
                <select 
                  value={editLevel} 
                  onChange={(e) => setEditLevel(e.target.value)} 
                  className="compact-select"
                  disabled={showInvisibleMessage}
                >
                  {levels.map(level => (
                    <option key={level} value={level}>{getLevelName(level, customLevels)}</option>
                  ))}
                </select>
              </div>

              {/* Group display (read-only) */}
              <div className="control-row">
                <label>Group:</label>
                <div className="group-display">{groupName === 'No' ? 'No' : groupName}</div>
              </div>

              {/* Stored override flag (read-only) */}
              <div className="control-row">
                <label>Override:</label>
                <div className="group-display">{storedCornerOverride ? 'Yes' : 'No'}</div>
              </div>

              <div className="control-row corner-override-row">
                <label>Corner Override:</label>
                <div className="corner-override-controls">
                  <select
                    value={cornerMode}
                    onChange={(e) => setCornerMode(e.target.value)}
                    className="compact-select corner-select"
                    disabled={showInvisibleMessage}
                  >
                    <option value="Auto">Auto</option>
                    <option value="Inline">Inline</option>
                    <option value="Corner">Corner</option>
                  </select>
                </div>
              </div>
              {/* move custom number section below corner override */}
              <div className="number-override-section">
                <h5>Custom Number</h5>
                <label className="checkbox-label">
                  <input type="checkbox" checked={numberOverride} onChange={(e) => setNumberOverride(e.target.checked)} />
                  <span>Override booth number</span>
                </label>
                {numberOverride && (
                  <div className="number-override-input">
                    <input type="text" value={manualNumber} onChange={(e) => setManualNumber(e.target.value)} />
                    <button className="clear-number-override" onClick={handleClearNumberOverride}>Clear</button>
                  </div>
                )}
              </div>
            </div>

            {/* Level preview with color and pricing */}
            <div className="level-preview">
              <div className="level-info">
                <div className="level-color-name">
                  <div 
                    className="color-swatch" 
                    style={{ 
                      backgroundColor: getLevelColor(editLevel, customLevels),
                      border: '2px solid #ddd'
                    }}
                  ></div>
                  <span className="level-name">{getLevelName(editLevel, customLevels)}</span>
                </div>
                <div className="pricing-info">
                  <div className="price-item">
                    <span className="price-label">Inline:</span>
                    <span className="price-value">{formatCentsToUSD(getLevelPricing(editLevel, false, customLevels))}</span>
                  </div>
                  <div className="price-item">
                    <span className="price-label">Corner:</span>
                    <span className="price-value">{formatCentsToUSD(getLevelPricing(editLevel, true, customLevels))}</span>
                  </div>
                  <div className="current-price">
                    <span className="current-label">Current Price:</span>
                    <span className="current-value">
                      {formatCentsToUSD(getLevelPricing(editLevel, (cornerMode === 'Corner') ? true : (cornerMode === 'Inline') ? false : (getEffectiveCornerValue(selectedCell) === 'Y'), customLevels))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Action buttons */}
            <div className="edit-actions compact-actions">
              <button 
                className="apply-btn compact-btn" 
                onClick={handleApply}
                disabled={showInvisibleMessage}
              >
                Apply
              </button>
              <button className="cancel-btn compact-btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        </div>

        
      </div>
    </div>
  );
};

const LayoutManager = ({ show, onClose, savedLayouts, onSave, onSaveAsCopy, onLoad, onDelete, onUpdateStatus, layoutName, setLayoutName, layoutStatus, setLayoutStatus, currentLayout }) => {
  if (!show) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return '#f39c12';
      case 'Published': return '#27ae60';
      case 'Inactive': return '#95a5a6';
      default: return '#f39c12';
    }
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content layout-manager" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <h3>Layout Manager</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="popup-body">
          <div className="layout-manager-section">
            {/* Save New Layout */}
            <div className="save-layout-section">
              <h4>Save Current Layout</h4>
              <div className="save-controls">
                <input
                  type="text"
                  placeholder="Enter layout name..."
                  value={layoutName}
                  onChange={(e) => setLayoutName(e.target.value)}
                  className="layout-name-input"
                />
                <select 
                  value={layoutStatus} 
                  onChange={(e) => setLayoutStatus(e.target.value)}
                  className="layout-status-select"
                >
                  <option value="Draft">Draft</option>
                  <option value="Published">Published</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <button onClick={onSave} className="save-layout-btn">
                  Save Layout
                </button>
                <button onClick={onSaveAsCopy} className="save-copy-btn" title="Create a new layout from this one without overwriting the original">
                  Save as Copy
                </button>
              </div>
            </div>

            {/* Current Layout Info */}
            {currentLayout && (
              <div className="current-layout-section">
                <h4>Current Layout</h4>
                <div className="current-layout-info">
                  <span className="layout-name">{currentLayout.name}</span>
                  <span 
                    className="layout-status" 
                    style={{ backgroundColor: getStatusColor(currentLayout.status) }}
                  >
                    {currentLayout.status}
                  </span>
                </div>
              </div>
            )}

            {/* Saved Layouts */}
            <div className="saved-layouts-section">
              <h4>Saved Layouts ({savedLayouts.length})</h4>
              <div className="layouts-list">
                {savedLayouts.length === 0 ? (
                  <p className="no-layouts">No saved layouts yet.</p>
                ) : (
                  savedLayouts.map(layout => (
                    <div key={layout.id} className="layout-item">
                      <div className="layout-info">
                        <span className="layout-name">{layout.name}</span>
                        <span 
                          className="layout-status"
                          style={{ backgroundColor: getStatusColor(layout.status) }}
                        >
                          {layout.status}
                        </span>
                        <span className="layout-date">
                          {new Date(layout.updatedAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="layout-actions">
                        <button 
                          onClick={() => onLoad(layout)} 
                          className="load-btn"
                        >
                          Load
                        </button>
                        <select 
                          value={layout.status}
                          onChange={(e) => onUpdateStatus(layout.id, e.target.value)}
                          className="status-select"
                        >
                          <option value="Draft">Draft</option>
                          <option value="Published">Published</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                        <button 
                          onClick={() => onDelete(layout.id)} 
                          className="delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Custom number popup removed — functionality now in PropertyPopup

const LevelManager = ({ show, onClose, customLevels, onLevelsUpdate, currentLayoutId, currentUserId }) => {
  // Convert levels from cents -> dollars for editing UI
  const convertLevels = (src) => {
    const out = {};
    Object.entries(src || {}).forEach(([k, v]) => {
      out[k] = {
        ...v,
        inlinePrice: (typeof v.inlinePrice === 'number') ? (v.inlinePrice / 100) : (v.inlinePrice || 0),
        cornerPrice: (typeof v.cornerPrice === 'number') ? (v.cornerPrice / 100) : (v.cornerPrice || 0)
      };
    });
    return out;
  };

  const [levels, setLevels] = useState(() => convertLevels(customLevels || {}));
  const [levelsOrder, setLevelsOrder] = useState(() => Object.keys(convertLevels(customLevels || {})));
  const [editingLevel, setEditingLevel] = useState(null);
  const originalLevelsRef = useRef(convertLevels(customLevels || {}));

  useEffect(() => {
    // Update UI values when incoming customLevels change and keep an original copy for change-detection
    const converted = convertLevels(customLevels || {});
    setLevels(converted);
    originalLevelsRef.current = converted;
    // If levels include sortOrder, use it to establish order; otherwise fallback to object key order
    const ordered = Object.entries(converted)
      .sort((a, b) => ((a[1].sortOrder || 0) - (b[1].sortOrder || 0)))
      .map(([k]) => k);
    setLevelsOrder(ordered);
  }, [customLevels]);

  if (!show) return null;

  const sanitizeLevelId = (name) => {
    if (!name) return '';
    return String(name).trim().replace(/\s+/g, '');
  };

  const ensureUniqueId = (desiredId, existingIds) => {
    let id = desiredId || '';
    if (!id) id = `Level${Date.now()}`;
    let candidate = id;
    let i = 1;
    while (existingIds.includes(candidate)) {
      candidate = `${id}${i}`;
      i += 1;
    }
    return candidate;
  };

  // Update an individual level field. Do not sanitize or rename on every keystroke.
  const handleLevelChange = (levelKey, field, value) => {
    setLevels(prev => ({
      ...prev,
      [levelKey]: {
        ...prev[levelKey],
        [field]: value
      }
    }));
  };

  // Toggle editing for a level. When finishing (Done), perform sanitization and ensure unique id/name.
  const handleToggleEdit = (levelKey) => {
    const isEditingNow = editingLevel === levelKey;
    if (!isEditingNow) {
      setEditingLevel(levelKey);
      return;
    }

    // User clicked Done — sanitize and ensure uniqueness, renaming the key if necessary
    const rawName = (levels[levelKey] && levels[levelKey].name) ? String(levels[levelKey].name) : levelKey;
    const sanitized = sanitizeLevelId(rawName) || `Level${Date.now()}`;
    const existing = Object.keys(levels).filter(k => k !== levelKey).map(k => sanitizeLevelId((levels[k] && levels[k].name) ? String(levels[k].name) : k));
    const uniqueId = ensureUniqueId(sanitized, existing);

    if (uniqueId !== levelKey) {
      setLevels(prev => {
        const next = { ...prev };
        const entry = { ...(next[levelKey] || {}) };
        entry.name = uniqueId;
        delete next[levelKey];
        next[uniqueId] = entry;
        return next;
      });
      setLevelsOrder(prev => prev.map(k => (k === levelKey ? uniqueId : k)));
    } else {
      // Ensure stored name is the sanitized unique id (no spaces)
      setLevels(prev => ({ ...prev, [levelKey]: { ...(prev[levelKey] || {}), name: uniqueId } }));
    }
    // Close editor
    setEditingLevel(null);
  };

    const moveLevel = (levelKey, direction) => {
      setLevelsOrder(prev => {
        const idx = prev.indexOf(levelKey);
        if (idx === -1) return prev;
        const newIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= prev.length) return prev;
        const copy = prev.slice();
        const [item] = copy.splice(idx, 1);
        copy.splice(newIdx, 0, item);
        return copy;
      });
    };

  const addNewLevel = () => {
    // Create a unique name without spaces and use it as the key/id
    const baseName = `NewLevel`;
    const existing = Object.keys(levels).map(k => k);
    const newId = ensureUniqueId(baseName, existing);
    setLevels(prev => ({
      ...prev,
      [newId]: {
        name: newId,
        color: '#3498db',
        inlinePrice: 3.00,
        cornerPrice: 3.75
      }
    }));
    setLevelsOrder(prev => ([...prev, newId]));
    setEditingLevel(newId);
  };

  const removeLevel = (levelKey) => {
    // Allow removal of any level; defaults are now provided from server
    
    if (window.confirm(`Are you sure you want to remove the "${levels[levelKey].name}" level?`)) {
      const newLevels = { ...levels };
      delete newLevels[levelKey];
      setLevels(newLevels);
      setLevelsOrder(prev => prev.filter(k => k !== levelKey));
    }
  };

  const resetToDefaults = () => {
    if (!window.confirm('Are you sure you want to reset all levels to default values? This will remove any custom levels you created.')) return;

    (async () => {
      try {
        const defs = await getLayoutDefaults();
        const src = (defs && defs.customLevels) ? defs.customLevels : {};

        // Normalize defaults into an object map and preserve order
        const defaultsMap = {};
        let order = [];

        if (Array.isArray(src)) {
          // src is an ordered array of level objects
          src.forEach((entry, idx) => {
            const key = entry.id || entry.name || (`Level${idx}`);
            defaultsMap[key] = { ...entry };
            order.push(key);
          });
        } else if (src && typeof src === 'object') {
          Object.entries(src).forEach(([k, v]) => {
            defaultsMap[k] = { ...v };
          });
          // establish order from sortOrder if present, otherwise object key order
          order = Object.entries(defaultsMap)
            .sort((a, b) => ((a[1].sortOrder || 0) - (b[1].sortOrder || 0)))
            .map(([k]) => k);
        }

        // Convert cents -> dollars for UI editing
        const converted = {};
        Object.entries(defaultsMap).forEach(([k, v]) => {
          converted[k] = {
            ...v,
            inlinePrice: (typeof v.inlinePrice === 'number') ? (v.inlinePrice / 100) : (v.inlinePrice || 0),
            cornerPrice: (typeof v.cornerPrice === 'number') ? (v.cornerPrice / 100) : (v.cornerPrice || 0)
          };
        });

        setLevels(converted);
        setLevelsOrder((order && order.length > 0) ? order : Object.keys(converted));

      } catch (err) {
        console.error('[LevelManager] Failed to load defaults from appSettings/icnacon-defaults', err);
        window.alert('Failed to load default levels. See console for details.');
      }
    })();
  };

  const handleSave = () => {
    if (editingLevel) {
      alert('Please finish editing the level (click Done) before saving.');
      return;
    }
    // Convert all prices to cents before saving and normalize keys to sanitized unique names
    const levelsInCents = {};
    const existingIds = [];
    // Build in the user-specified order so order is preserved when saving
    const orderedKeys = (levelsOrder && levelsOrder.length > 0) ? levelsOrder : Object.keys(levels);
    orderedKeys.forEach((key, idx) => {
      const level = levels[key] || {};
      const rawName = level && level.name ? String(level.name) : key;
      const sanitized = sanitizeLevelId(rawName) || `Level${Date.now()}`;
      const uniqueId = ensureUniqueId(sanitized, existingIds);
      existingIds.push(uniqueId);
      levelsInCents[uniqueId] = {
        name: uniqueId,
        color: level.color || '#3498db',
        inlinePrice: (typeof level.inlinePrice === 'number' && !isNaN(level.inlinePrice)) ? Math.round(level.inlinePrice * 100) : 0,
        cornerPrice: (typeof level.cornerPrice === 'number' && !isNaN(level.cornerPrice)) ? Math.round(level.cornerPrice * 100) : 0,
        sortOrder: idx
      };
    });
    // Persist levels to DB immediately if we have layout & user context
    try {
      if (currentLayoutId && currentUserId) {
        // update only the customLevels field; sortOrder stored inside each level
        const payload = { customLevels: levelsInCents };
        updateLayoutInDB(currentLayoutId, payload, currentUserId).catch(err => {
          console.error('[LevelManager] Failed to persist levels to DB', err);
        });
      }
    } catch (e) {
      console.error('[LevelManager] Error persisting levels to DB', e);
    }
    onLevelsUpdate(levelsInCents);
    onClose();
  };

  const hasLevelChanges = () => {
    try {
      return JSON.stringify(originalLevelsRef.current) !== JSON.stringify(levels);
    } catch (e) {
      return true;
    }
  };

  const handleCancel = () => {
    if (editingLevel) {
      alert('Please finish editing the level (click Done) before closing.');
      return;
    }
    if (hasLevelChanges()) {
      if (!window.confirm('You have unsaved changes to Levels. Discard changes and close?')) {
        return; // keep open
      }
    }
    // Reset to original converted values and close
    setLevels(originalLevelsRef.current ? { ...originalLevelsRef.current } : convertLevels(customLevels || {}));
    onClose();
  };

  return (
    <div className="popup-overlay" onClick={handleCancel}>
      <div className="popup-content level-manager" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <h3>Level Manager</h3>
          <button className="close-btn" onClick={handleCancel}>×</button>
        </div>
        
        <div className="popup-body">
          <div className="level-manager-section">
            <div className="level-manager-header">
              <p>Define custom levels with colors and pricing for inline and corner booths.</p>
              <div className="level-actions">
                <button onClick={resetToDefaults} className="reset-levels-btn">
                  🔄 Reset to Defaults
                </button>
              </div>
            </div>

            <div className="levels-list">
              {levelsOrder.map((levelKey) => {
                const level = levels[levelKey] || {};
                const isDefault = false;
                const isEditing = editingLevel === levelKey;
                
                return (
                  <div key={levelKey} className={`level-item ${isDefault ? 'default-level' : 'custom-level'}`}>
                    <div className="level-header">
                      <div className="level-info">
                        <div 
                          className="level-color-preview"
                          style={{ backgroundColor: level.color }}
                        ></div>
                        <span className="level-name">{level.name}</span>
                        {isDefault && (
                          <span className="default-badge">
                            {levelKey}
                          </span>
                        )}
                      </div>
                      <div className="level-controls">
                        <button 
                          onClick={() => handleToggleEdit(levelKey)}
                          className="edit-level-btn"
                        >
                          {isEditing ? 'Done' : 'Edit'}
                        </button>
                        <button className="move-up-btn" style={{ minWidth: '36px', padding: '4px 8px', marginLeft: 6 }} onClick={() => moveLevel(levelKey, 'up')} title="Move up">↑</button>
                        <button className="move-down-btn" style={{ minWidth: '36px', padding: '4px 8px', marginLeft: 6 }} onClick={() => moveLevel(levelKey, 'down')} title="Move down">↓</button>
                        {!isDefault && (
                          <button 
                            onClick={() => removeLevel(levelKey)}
                            className="remove-level-btn"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    {isEditing && (
                      <div className="level-editor">
                        <div className="level-editor-grid">
                          <div className="editor-field">
                            <label>Name:</label>
                            <input
                              type="text"
                              value={level.name}
                              onChange={(e) => handleLevelChange(levelKey, 'name', e.target.value)}
                              className="level-name-input"
                            />
                          </div>

                          <div className="editor-field">
                            <label>Color:</label>
                            <div className="color-input-group">
                              <input
                                type="color"
                                value={level.color}
                                onChange={(e) => handleLevelChange(levelKey, 'color', e.target.value)}
                                className="level-color-input"
                              />
                              <input
                                type="text"
                                value={level.color}
                                onChange={(e) => handleLevelChange(levelKey, 'color', e.target.value)}
                                className="level-color-text"
                                placeholder="#ffffff"
                              />
                            </div>
                          </div>

                          <div className="editor-field">
                            <label>Inline Booth Price:</label>
                            <div className="price-input-group">
                              <span className="currency-symbol">$</span>
                                <input
                                  type="number"
                                  value={level.inlinePrice}
                                  onChange={(e) => handleLevelChange(levelKey, 'inlinePrice', Math.max(0, parseFloat(e.target.value) || 0))}
                                  className="price-input"
                                  min="0"
                                  step="0.01"
                                />
                            </div>
                          </div>

                          <div className="editor-field">
                            <label>Corner Booth Price:</label>
                            <div className="price-input-group">
                              <span className="currency-symbol">$</span>
                                <input
                                  type="number"
                                  value={level.cornerPrice}
                                  onChange={(e) => handleLevelChange(levelKey, 'cornerPrice', Math.max(0, parseFloat(e.target.value) || 0))}
                                  className="price-input"
                                  min="0"
                                  step="0.01"
                                />
                            </div>
                          </div>
                        </div>

                        <div className="level-preview">
                            <div className="preview-booth inline-booth" style={{ backgroundColor: level.color }}>
                              <span className="booth-label">Inline</span>
                              <span className="booth-price">{(typeof level.inlinePrice === 'number') ? level.inlinePrice.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</span>
                            </div>
                            <div className="preview-booth corner-booth" style={{ backgroundColor: level.color, border: '2px solid #000' }}>
                              <span className="booth-label">Corner</span>
                              <span className="booth-price">{(typeof level.cornerPrice === 'number') ? level.cornerPrice.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}</span>
                            </div>
                        </div>
                      </div>
                    )}

                    {!isEditing && (
                      <div className="level-summary">
                        <div className="pricing-summary">
                          <span className="price-item">Inline: {(typeof level.inlinePrice === 'number') ? `$${Number(level.inlinePrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}</span>
                          <span className="price-separator">•</span>
                          <span className="price-item">Corner: {(typeof level.cornerPrice === 'number') ? `$${Number(level.cornerPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
              <button onClick={addNewLevel} className="add-level-btn">
                + Add Custom Level
              </button>
            </div>
            <div className="level-manager-actions">
              <button onClick={handleSave} className="save-levels-btn">
                Save Levels
              </button>
              <button onClick={handleCancel} className="cancel-levels-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NumberingSchemePopup = ({ show, onClose, onApply, currentScheme = 'alpha-numeric', currentSkipEmptyRows = false }) => {
  const [selectedScheme, setSelectedScheme] = useState(currentScheme);
  const [skipEmptyRows, setSkipEmptyRows] = useState(currentSkipEmptyRows);

  // Update internal state when props change (e.g., when popup is reopened with different current values)
  useEffect(() => {
    setSelectedScheme(currentScheme);
    setSkipEmptyRows(currentSkipEmptyRows);
  }, [currentScheme, currentSkipEmptyRows]);

  const schemes = [
    {
      id: 'four-digit-numbers',
      name: '4-Digit Numbering',
      description: 'Use 4-digit numbers that are fixed to their location, based on row and column position.',
      example: '0101, 0102, 0103...'
    },
    {
      id: 'four-digit-alternating-rows',
      name: '4-Digit Alternating Even/Odd Rows',
      description: 'Like 4-digit numbering but alternates even and odd column numbers by row: odd rows get odd column numbers, even rows get even column numbers.',
      example: '0101, 0103, 0105 (row 1) / 0202, 0204, 0206 (row 2)'
    },
    {
      id: 'alpha-numeric',
      name: 'Alpha Numeric Numbering',
      description: 'Row letters (A-Z, then AA-ZZ) with alternating odd/even columns per display pair (first line odd cols, second line even cols).',
      example: 'A01, A03, A05 / B01, B03, B05 / B02, B04, B06 / C01...'
    },
    {
      id: 'sequential-numbers',
      name: 'Sequential Numbers',
      description: 'Sequential numbers across the grid, starting with three digits. It will only number those cells that are visible, making the count same as the number of cells.',
      example: '001, 002, 003...'
    }
  ];

  if (!show) return null;

  const handleApply = () => {
    onApply(selectedScheme, skipEmptyRows);
    onClose();
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content numbering-scheme-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <h3>Booth Numbering Scheme</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="popup-body">
          <div className="numbering-scheme-section">
            <p>Choose how booth numbers should be generated:</p>
            
            <div className="schemes-list">
              {schemes.map(scheme => (
                <div key={scheme.id} className={`scheme-option ${selectedScheme === scheme.id ? 'selected' : ''}`}>
                  <label className="scheme-label">
                    <input
                      type="radio"
                      name="numbering-scheme"
                      value={scheme.id}
                      checked={selectedScheme === scheme.id}
                      onChange={(e) => setSelectedScheme(e.target.value)}
                    />
                    <div className="scheme-info">
                      <h4>{scheme.name}</h4>
                      <p className="scheme-description">{scheme.description}</p>
                      <div className="scheme-example">
                        <strong>Example:</strong> {scheme.example}
                      </div>
                    </div>
                  </label>
                </div>
              ))}
            </div>

            <div className="numbering-options">
              <h4>Numbering Options</h4>
              <div className="numbering-checkboxes">
                <label className="checkbox-label">
                  <div>
                    <input
                      type="checkbox"
                      checked={skipEmptyRows}
                      onChange={(e) => setSkipEmptyRows(e.target.checked)}
                    />
                    <span>Skip Empty Rows</span>
                  </div>
                  <small>Only count rows that contain visible cells when generating numbers</small>
                </label>

              </div>
            </div>

            <div className="numbering-actions">
              <button onClick={handleApply} className="apply-numbering-btn">
                Apply Numbering Scheme
              </button>
              <button onClick={onClose} className="cancel-numbering-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BulkPropertyEditor = ({ selectedCells, gridData, onClose, show, onApplyChanges, customLevels }) => {
  const [bulkLevel, setBulkLevel] = useState('');
  const [showInvisibleMessage, setShowInvisibleMessage] = useState(false);
  const [cornerMode, setCornerMode] = useState('Auto');
  const levels = Object.keys(customLevels || {}).filter(level => level !== 'Invisible'); // Filter out 'Invisible'

  const selectedCellsArray = Array.from(selectedCells).map(cellId => 
    gridData.find(cell => cell.id === cellId)
  ).filter(Boolean);


  // Reset form values when popup opens
  React.useEffect(() => {
    if (show) {
      setBulkLevel('');  // Reset to "Keep Current"
      setCornerMode('Auto');
    }
  }, [show]);

  // Check if any selected cells are invisible
  React.useEffect(() => {
    if (show && selectedCells.size > 0) {
      const hasInvisibleCells = selectedCellsArray.some(cell => 
        cell && (cell.status === 'Invisible' || cell.level === 'Invisible')
      );
      setShowInvisibleMessage(hasInvisibleCells);
    }
  }, [selectedCells, gridData, show]);

  if (!show || selectedCells.size === 0) return null;

  const handleApply = () => {
    const changes = {};
    
    if (bulkLevel) {
      changes.level = bulkLevel;
      // Color is now derived from level - no need to set it
    }
    
    // Handle corner override in bulk
    if (cornerMode === 'Auto') {
      // Clear override for selection
      changes.cornerOverride = false;
      changes.manualCorner = undefined;
    } else {
      const desired = cornerMode === 'Corner' ? 'Y' : 'N';
      changes.cornerOverride = true;
      changes.corner = desired;
      changes.manualCorner = desired;
    }
    
    onApplyChanges(changes);
    onClose();
  };

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content bulk-editor compact-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header compact-header">
          <h4>Bulk Edit ({selectedCells.size} cells)</h4>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="popup-body compact">
          <div className="property-section compact-property-section">
            {/* Header with selection info */}
            <div className="booth-header">
              <div className="booth-title">
                <strong>Bulk Edit</strong>
                <span className="selection-count">{selectedCells.size} cells selected</span>
              </div>
            </div>

            {/* Selected cells preview */}
            <div className="selected-cells-preview">
              <div className="cell-preview-list">
                {selectedCellsArray.slice(0, 8).map(cell => (
                  <div key={cell.id} className="cell-preview" style={{ backgroundColor: getLevelColor(cell.level, customLevels) }}>
                    {cell.number}
                  </div>
                ))}
                {selectedCells.size > 8 && <div className="cell-preview more">+{selectedCells.size - 8}</div>}
              </div>
            </div>

            {/* Create-group UI removed from Bulk Editor (feature reverted) */}

            {/* Message for invisible cells */}
            {showInvisibleMessage && (
              <div className="invisible-message">
                <div className="message-content">
                  <strong>⚠️ Some selected cells are invisible</strong>
                  <p>To edit invisible cells, please use the "Toggle Invisible" button first to make them visible.</p>
                </div>
              </div>
            )}

            {/* Compact controls */}
            <div className="compact-controls">
              <div className="control-row">
                <label>Level:</label>
                <select 
                  value={bulkLevel} 
                  onChange={(e) => setBulkLevel(e.target.value)} 
                  className="compact-select"
                  disabled={showInvisibleMessage}
                >
                  <option value="">Keep Current</option>
                  {levels.map(level => (
                    <option key={level} value={level}>{getLevelName(level, customLevels)}</option>
                  ))}
                </select>
              </div>
              <div className="control-row">
                <label>Corner Override:</label>
                <select
                  value={cornerMode}
                  onChange={(e) => setCornerMode(e.target.value)}
                  className="compact-select corner-select"
                  disabled={showInvisibleMessage}
                >
                  <option value="Auto">Auto</option>
                  <option value="Inline">Inline</option>
                  <option value="Corner">Corner</option>
                </select>
              </div>
            </div>

            {/* Level preview with pricing - only show if level is selected */}
            {bulkLevel && (
              <div className="level-preview">
                <div className="level-info">
                  <div className="level-color-name">
                    <div 
                      className="color-swatch" 
                      style={{ 
                        backgroundColor: getLevelColor(bulkLevel, customLevels),
                        border: '2px solid #ddd'
                      }}
                    ></div>
                    <span className="level-name">{getLevelName(bulkLevel, customLevels)}</span>
                  </div>
                  <div className="pricing-info">
                    <div className="price-item">
                      <span className="price-label">Inline:</span>
                      <span className="price-value">{formatCentsToUSD(getLevelPricing(bulkLevel, false, customLevels))}</span>
                    </div>
                    <div className="price-item">
                      <span className="price-label">Corner:</span>
                      <span className="price-value">{formatCentsToUSD(getLevelPricing(bulkLevel, true, customLevels))}</span>
                    </div>
                    <div className="bulk-note">
                      <span className="note-text">These prices will be applied based on each cell's corner status</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="edit-actions compact-actions">
              <button 
                className="apply-btn compact-btn" 
                onClick={handleApply}
                disabled={showInvisibleMessage}
              >
                Apply to All ({selectedCells.size})
              </button>
              <button className="cancel-btn compact-btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* visual booth picker removed — groups are created from the current multi-selection on the layout */}
    </div>
  );
};

const LayoutBuilder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [rows, setRows] = useState(40);
  const [columns, setColumns] = useState(30);
  const [inputRows, setInputRows] = useState(40); // Input field values
  const [inputColumns, setInputColumns] = useState(30); // Input field values
  const [statusReset, setStatusReset] = useState(false); // Reset status when regenerating
  const [levelReset, setLevelReset] = useState(false); // Reset level when regenerating
  const [cellSize, setCellSize] = useState(30);
  const [cellPadding, setCellPadding] = useState(5);
  const [fontSize, setFontSize] = useState(12);
  const [layoutMapUrl, setLayoutMapUrl] = useState('');
  const [surroundings, setSurroundings] = useState({ top: [], right: [], bottom: [], left: [], corners: {} });
  const [surroundingsBgColor, setSurroundingsBgColor] = useState('#f0f4f8');
  const [surroundingsTextColor, setSurroundingsTextColor] = useState('#111111');
  const [surroundingsFontSize, setSurroundingsFontSize] = useState(14);
  const [gridData, setGridData] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [showPopup, setShowPopup] = useState(false);
  const [showBulkEditor, setShowBulkEditor] = useState(false);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  
  
  // Layout management
  const [currentLayout, setCurrentLayout] = useState(null);
  const [savedLayouts, setSavedLayouts] = useState([]);
  const [showLayoutManager, setShowLayoutManager] = useState(false);
  const [layoutName, setLayoutName] = useState('');
  const [layoutStatus, setLayoutStatus] = useState('Draft');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

  // Level management
  const [customLevels, setCustomLevels] = useState(undefined);
  const [showLevelManager, setShowLevelManager] = useState(false);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [appDefaults, setAppDefaults] = useState(null);
  // EventAdmin selector for layouts
  const [profiles, setProfiles] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [layoutEventAdmins, setLayoutEventAdmins] = useState([]);


  // Normalize level price values to cents before storing in layout state.
  // This prevents accidental double-conversion when different parts of the app
  // supply values in either dollars (e.g. 895.00) or cents (e.g. 89500).
  const normalizeLevelsToCents = (levelsObj) => {
    if (!levelsObj || typeof levelsObj !== 'object') return {};
    const out = {};
    Object.entries(levelsObj).forEach(([k, v]) => {
      const inlineRaw = v?.inlinePrice ?? 0;
      const cornerRaw = v?.cornerPrice ?? 0;
      const norm = (val) => {
        const n = Number(val) || 0;
        // If value has fractional part, treat as dollars and convert to cents
        if (Math.abs(n - Math.round(n)) > 0) return Math.round(n * 100);
        // If integer and reasonably large, assume it's already in cents
        if (n >= 10000) return Math.round(n);
        // If integer and divisible by 100, likely cents (e.g. 89500)
        if (n >= 100 && n % 100 === 0) return Math.round(n);
        // Otherwise treat as dollars and convert
        return Math.round(n * 100);
      };
      out[k] = {
        ...v,
        inlinePrice: norm(inlineRaw),
        cornerPrice: norm(cornerRaw)
      };
    });
    return out;
  };

  // Numbering scheme management
  const [numberingScheme, setNumberingScheme] = useState('alpha-numeric'); // Default scheme
  const [skipEmptyRows, setSkipEmptyRows] = useState(false);
  const [showNumberingScheme, setShowNumberingScheme] = useState(false);

  // Rules Admin (for configuring rules as part of layout)
  const [showRulesAdmin, setShowRulesAdmin] = useState(false);
  const [rulesConfig, setRulesConfig] = useState(() => {
    // If starting a new layout, enable all rules and no booth groups
    if (location.state && location.state.newLayoutName) {
      // For a new layout, start from the default BOOTH_RULES_CONFIG
      // and do not auto-create booth groups. Keep each rule's default
      // `enabled` value as defined in the config (so disabled rules
      // remain disabled by default).
      const defaults = {};
      Object.entries(BOOTH_RULES_CONFIG).forEach(([key, rule]) => {
        defaults[key] = { ...rule };
        if (defaults[key].groups) defaults[key].groups = undefined;
      });
      return defaults;
    }
    if (location.state && location.state.existingLayout && location.state.existingLayout.rules) {
      const layout = location.state.existingLayout;
      const dbRules = layout.rules;
      const dbBoothGroups = layout.boothGroups || null;
      const base = { ...BOOTH_RULES_CONFIG };
      // Build a fresh config with all disabled, no groups
      const config = Object.keys(base).reduce((acc, key) => {
        acc[key] = { ...base[key], enabled: false, groups: undefined };
        return acc;
      }, {});
      const resolveRuleKey = (idOrKey) => {
        if (!idOrKey) return undefined;
        if (config[idOrKey]) return idOrKey;
        const match = Object.entries(config).find(([, rule]) => rule.id === idOrKey);
        return match ? match[0] : undefined;
      };
      // Load rule enablement + parameters
      dbRules.forEach(({ ruleId, parameters }) => {
        const key = resolveRuleKey(ruleId);
        if (!key || !config[key]) return;
        config[key].enabled = true;
        config[key].parameters = parameters;
        // Merge saved simple parameters back onto top-level for UI editing
        if (parameters && typeof parameters === 'object') {
          Object.entries(parameters).forEach(([k, v]) => {
            if (k === 'groups') return;
            if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
              config[key][k] = v;
            }
          });
        }
      });
      // Preferred: load booth groups from top-level field
      if (dbBoothGroups && typeof dbBoothGroups === 'object') {
        Object.entries(dbBoothGroups).forEach(([ruleKeyOrId, groups]) => {
          const key = resolveRuleKey(ruleKeyOrId);
          if (!key || !config[key]) return;
          config[key].groups = Array.isArray(groups) ? groups : undefined;
        });
      } else {
        // Legacy fallback: groups stored inside rules entries
        dbRules.forEach(({ ruleId, groups }) => {
          const key = resolveRuleKey(ruleId);
          if (key && Array.isArray(groups)) {
            config[key].groups = groups;
          }
        });
      }
      return config;
    }
    return CUSTOM_BOOTH_RULES;
  });

  // Grouping/overlay UI has been disabled for this layout — stubbed out
  const computeGroupOverlays = () => [];

  

  // Read-only lock when status is not Draft
  const isLocked = (layoutStatus || '').toLowerCase() !== 'draft';

  // Name uniqueness helpers: prompt user to choose a different name instead of auto-suffixing
  const fetchAllLayouts = async () => {
    try {
      const { listAllLayouts } = await import('../services/layoutsService');
      return await listAllLayouts();
    } catch (e) {
      return savedLayouts || [];
    }
  };

  const isLayoutNameTaken = async (name, currentId = null) => {
    const allLayouts = await fetchAllLayouts();
    const normalize = (s) => (s || '').trim().toLowerCase();
    const target = normalize(name);
    return allLayouts.some(l => (!currentId || l.id !== currentId) && normalize(l.name) === target);
  };

  const resolveUniqueNameInteractive = async (initialName, currentId = null) => {
    let proposed = (initialName || '').trim();
    if (!proposed) return proposed;
    while (await isLayoutNameTaken(proposed, currentId)) {
      const input = window.prompt(`A layout named "${proposed}" already exists. Please enter a different name:`, proposed);
      if (input === null) {
        // User cancelled
        return null;
      }
      proposed = (input || '').trim();
      if (!proposed) {
        const retry = window.confirm('Name cannot be empty. Do you want to try again?');
        if (!retry) return null;
      }
    }
    return proposed;
  };

  // Generate initial grid data
  useEffect(() => {
    // Only generate initial grid if we don't have any grid data. Wait until
    // defaults have been loaded to ensure dimensions and numbering options
    // reflect any server-provided defaults.
    if (gridData.length === 0 && defaultsLoaded) {
      const newGridData = [];
      // Use the input fields (which reflect loaded defaults) when creating
      // the initial grid. This avoids building the grid from an unrelated
      // internal `rows`/`columns` value (which may default to a large number).
      for (let row = 1; row <= inputRows; row++) {
        for (let col = 1; col <= inputColumns; col++) {
          newGridData.push(generateCellData(row, col, inputColumns));
        }
      }

      // Update corner flags for the initial grid
      const finalGridData = updateCornerFlags(newGridData);
      // Apply numbering so initial load uses bottom-up numbering (A/01 at bottom)
      const numberedGridData = applyBoothNumbering(finalGridData, numberingScheme, skipEmptyRows, inputRows, inputColumns);

      // Sync the canonical rows/columns state with the input values so
      // subsequent operations that rely on `rows`/`columns` behave
      // consistently with the visible inputs.
      setRows(inputRows);
      setColumns(inputColumns);

      setGridData(numberedGridData);
    }
  }, [inputRows, inputColumns, defaultsLoaded]);

  const handleCellClick = (cellData, event) => {
    if (isLocked) return; // editing disabled for Published/Inactive
    // All cells (including invisible ones) are selectable in multi-select mode
    if (isMultiSelectMode) {
      // Multi-select mode or booth selection mode
      const newSelectedCells = new Set(selectedCells);
      if (newSelectedCells.has(cellData.id)) {
        newSelectedCells.delete(cellData.id);
      } else {
        newSelectedCells.add(cellData.id);
      }
      setSelectedCells(newSelectedCells);
      
      // Clear row/column selections when manually selecting individual cells
      setSelectedRows(new Set());
      setSelectedColumns(new Set());
    } else {
      // Single select mode - show popup
      setSelectedCell(cellData);
      setShowPopup(true);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedCell(null);
  };

  const closeBulkEditor = () => {
    setShowBulkEditor(false);
  };

  const toggleMultiSelectMode = () => {
    if (isLocked) return; // disallow enabling multi-select when locked
    setIsMultiSelectMode(!isMultiSelectMode);
    setSelectedCells(new Set());
  };

  const clearSelection = () => {
    setSelectedCells(new Set());
    setSelectedRows(new Set());
    setSelectedColumns(new Set());
  };

  const selectAll = () => {
    const allCellIds = new Set(
      gridData.map(cell => cell.id)
    );
    setSelectedCells(allCellIds);
    setSelectedRows(new Set());
    setSelectedColumns(new Set());
  };

  const selectRow = (rowNumber, event) => {
    if (isLocked) return;
    const newSelectedRows = new Set(selectedRows);
    const newSelectedCells = new Set(selectedCells);
    
    if (event && (event.ctrlKey || event.metaKey)) {
      // Multi-select with Ctrl/Cmd key
      if (newSelectedRows.has(rowNumber)) {
        // Deselect row
        newSelectedRows.delete(rowNumber);
        // Remove cells from this row (including invisible ones)
        const rowCellIds = gridData
          .filter(cell => cell.row === rowNumber)
          .map(cell => cell.id);
        rowCellIds.forEach(id => newSelectedCells.delete(id));
      } else {
        // Select row
        newSelectedRows.add(rowNumber);
        // Add cells from this row (including invisible ones)
        const rowCellIds = gridData
          .filter(cell => cell.row === rowNumber)
          .map(cell => cell.id);
        rowCellIds.forEach(id => newSelectedCells.add(id));
      }
    } else {
      // Single select (clear others)
      newSelectedRows.clear();
      setSelectedColumns(new Set()); // Clear column selection
      newSelectedRows.add(rowNumber);
      
      // Select only this row's cells (including invisible ones)
      const rowCellIds = new Set(
        gridData
          .filter(cell => cell.row === rowNumber)
          .map(cell => cell.id)
      );
      newSelectedCells.clear();
      rowCellIds.forEach(id => newSelectedCells.add(id));
    }
    
    setSelectedRows(newSelectedRows);
    setSelectedCells(newSelectedCells);
  };

  const selectColumn = (colNumber, event) => {
    if (isLocked) return;
    const newSelectedColumns = new Set(selectedColumns);
    const newSelectedCells = new Set(selectedCells);
    
    if (event && (event.ctrlKey || event.metaKey)) {
      // Multi-select with Ctrl/Cmd key
      if (newSelectedColumns.has(colNumber)) {
        // Deselect column
        newSelectedColumns.delete(colNumber);
        // Remove cells from this column (including invisible ones)
        const colCellIds = gridData
          .filter(cell => cell.col === colNumber)
          .map(cell => cell.id);
        colCellIds.forEach(id => newSelectedCells.delete(id));
      } else {
        // Select column
        newSelectedColumns.add(colNumber);
        // Add cells from this column (including invisible ones)
        const colCellIds = gridData
          .filter(cell => cell.col === colNumber)
          .map(cell => cell.id);
        colCellIds.forEach(id => newSelectedCells.add(id));
      }
    } else {
      // Single select (clear others)
      newSelectedColumns.clear();
      setSelectedRows(new Set()); // Clear row selection
      newSelectedColumns.add(colNumber);
      
      // Select only this column's cells (including invisible ones)
      const colCellIds = new Set(
        gridData
          .filter(cell => cell.col === colNumber)
          .map(cell => cell.id)
      );
      newSelectedCells.clear();
      colCellIds.forEach(id => newSelectedCells.add(id));
    }
    
    setSelectedColumns(newSelectedColumns);
    setSelectedCells(newSelectedCells);
  };

  const openBulkEditor = () => {
    if (isLocked) return;
    if (selectedCells.size > 0) {
      setShowBulkEditor(true);
    }
  };

  const applyBulkChanges = (changes) => {
    if (isLocked) return;
    const updatedGridData = gridData.map(cell => {
      if (selectedCells.has(cell.id)) {
        return { ...cell, ...changes };
      }
      return cell;
    });
    
    // Update corner flags after changes (in case visibility changed)
    const finalGridData = updateCornerFlags(updatedGridData);
    setGridData(finalGridData);
    setSelectedCells(new Set());
    setHasUnsavedChanges(true);
  };

  const applySingleCellChanges = (cellId, changes) => {
    if (isLocked) return;
    const updatedGridData = gridData.map(cell => {
      if (cell.id === cellId) {
        return { ...cell, ...changes };
      }
      return cell;
    });
    
    // Update corner flags after changes (in case visibility changed)
    const finalGridData = updateCornerFlags(updatedGridData);
    setGridData(finalGridData);
    setHasUnsavedChanges(true);
    setSelectedCell(null);

    // If number override was cleared for this cell, reapply automatic numbering
    if (changes.hasOwnProperty('numberOverride') && changes.numberOverride === false) {
      reapplyNumbers(finalGridData);
    }
    if (changes.hasOwnProperty('manualNumber') && changes.manualNumber === undefined) {
      reapplyNumbers(finalGridData);
    }
  };

  // Level management functions
  const handleLevelsUpdate = (newLevels) => {
    if (isLocked) return;
    // Ensure incoming level definitions are normalized to cents to avoid
    // accidental double-conversion elsewhere in the save flow.
    // `LevelManager` now supplies prices already converted to cents.
    // Store them directly to `customLevels` to avoid double-conversion.
    setCustomLevels(newLevels);
    setHasUnsavedChanges(true);
    // Colors are now derived from levels - no need to update grid data
  };

  // Load defaults from Firestore (appsSettings/icnacon-defaults) on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
        try {
          const defs = await getLayoutDefaults();
          if (!defs || cancelled) {
          setDefaultsLoaded(true);
          return;
        }

          const isExistingLayout = !!(location && location.state && location.state.existingLayout);

          // Store raw defaults for potential rule merging
          setAppDefaults(defs);

          // Apply grid settings only for new layouts
          if (!isExistingLayout && defs.gridSettings) {
            setRows(defs.gridSettings.rows ?? rows);
            setColumns(defs.gridSettings.columns ?? columns);
            // Keep the input fields in sync with loaded defaults
            setInputRows(defs.gridSettings.rows ?? inputRows);
            setInputColumns(defs.gridSettings.columns ?? inputColumns);
            setCellSize(defs.gridSettings.cellSize ?? cellSize);
            setCellPadding(defs.gridSettings.cellPadding ?? cellPadding);
            setFontSize(defs.gridSettings.fontSize ?? fontSize);
            // Load layout map URL and surroundings from defaults if provided
            setLayoutMapUrl(defs.gridSettings.mapUrl ?? layoutMapUrl);
            try { setSurroundings(defs.gridSettings.surroundings ?? surroundings); } catch(e) {}
            setSurroundingsBgColor(defs.gridSettings.surroundingsBgColor ?? surroundingsBgColor);
            setSurroundingsTextColor(defs.gridSettings.surroundingsTextColor ?? surroundingsTextColor);
            setSurroundingsFontSize(defs.gridSettings.surroundingsFontSize ?? surroundingsFontSize);
          }

          // Numbering defaults only for new layouts
          if (!isExistingLayout && defs.numbering) {
            setNumberingScheme(defs.numbering.numberingScheme ?? numberingScheme);
            setSkipEmptyRows(typeof defs.numbering.skipEmptyRows === 'boolean' ? defs.numbering.skipEmptyRows : skipEmptyRows);
          }

          // Custom levels (normalize to cents) only for new layouts
          if (!isExistingLayout && defs.customLevels) {
            try {
              setCustomLevels(normalizeLevelsToCents(defs.customLevels));
            } catch (e) {
              console.warn('Failed to normalize customLevels from defaults', e);
              setCustomLevels(defs.customLevels);
            }
          }

          // Merge rule-level defaults (e.g., numeric limits, priority)
          // For new layouts: merge defaults onto the existing rulesConfig
          // For existing layouts: rebuild rulesConfig from the saved layout, using defaults as the base
          try {
            if (!isExistingLayout) {
              setRulesConfig(prev => applyDefaultsToRules(prev, defs));
            } else {
              // Try to obtain the layout being edited from navigation state or currentLayout
              const navLayout = location?.state?.existingLayout;
              const sourceLayout = navLayout || currentLayout || null;
              if (sourceLayout) {
                const cfg = buildRulesConfigFromLayout(sourceLayout, defs);
                setRulesConfig(cfg);
              } else {
                // No layout object available yet; apply defaults to the base rules so later loads benefit
                setRulesConfig(prev => applyDefaultsToRules(prev, defs));
              }
            }
          } catch (e) {
            console.warn('Failed to merge rule defaults:', e);
          }
      } catch (error) {
        console.warn('Failed to load layout defaults:', error);
      } finally {
        setDefaultsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);


  // When defaults finish loading, ensure the input fields reflect the current grid dimensions.
  useEffect(() => {
      if (defaultsLoaded) {
      setInputRows(rows);
      setInputColumns(columns);
    }
  }, [defaultsLoaded]);

  // Layout Functions
  const createLayoutData = (name, status = 'Draft') => {
    // Build rules list: ruleId and consolidated parameters (nested + simple top-level)
    const rules = Object.entries(rulesConfig)
      .filter(([key, rule]) => rule && rule.enabled)
      .map(([key, rule]) => {
        const consolidatedParams = { ...(rule.parameters || {}) };
        // Merge simple top-level fields into parameters to persist
        Object.entries(rule).forEach(([k, v]) => {
          if (['name','description','type','trigger','enabled','id','groups','parameters'].includes(k)) return;
          if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
            consolidatedParams[k] = v;
          }
        });
        return {
          ruleId: rule.id || key,
          parameters: consolidatedParams,
        };
      });

    // Collect booth groups separately per ruleId (only if present and non-empty)
    const boothGroups = Object.entries(rulesConfig).reduce((acc, [key, rule]) => {
      if (Array.isArray(rule?.groups) && rule.groups.length > 0) {
        acc[key] = rule.groups.map(g => ({ ...g }));
      }
      return acc;
    }, {});

    // Build a lookup of booth identifier -> group id so we can annotate each cell
    const boothToGroup = {};
    Object.values(boothGroups).forEach(groupArray => {
      if (!Array.isArray(groupArray)) return;
      groupArray.forEach(g => {
        const gid = g.id || (g.name ? g.name.replace(/\s+/g, '-').toLowerCase() : null);
        if (!gid) return;
        (Array.isArray(g.booths) ? g.booths : []).forEach(b => {
          boothToGroup[String(b)] = gid;
        });
      });
    });

    const sanitizeArr = (arr) => Array.isArray(arr) ? arr.map(s => (typeof s === 'string' ? s.trim() : String(s))).filter(Boolean) : [];

    const layout = {
      // No ID here - Firestore will generate the document ID
      name: name || `Layout_${Date.now()}`,
      status: status,
      gridSettings: {
        rows,
        columns,
        cellSize,
        cellPadding,
        fontSize,
        mapUrl: layoutMapUrl,
        surroundings: {
          top: sanitizeArr(surroundings.top),
          right: sanitizeArr(surroundings.right),
          bottom: sanitizeArr(surroundings.bottom),
          left: sanitizeArr(surroundings.left),
          corners: (surroundings && surroundings.corners) ? surroundings.corners : {}
        },
        surroundingsBgColor: surroundingsBgColor,
        surroundingsTextColor: surroundingsTextColor,
        surroundingsFontSize: surroundingsFontSize
      },
      customLevels: customLevels,
      numberingScheme: numberingScheme, // Save the numbering scheme
      skipEmptyRows: skipEmptyRows, // Save skip empty rows option
      cellData: gridData.map(cell => ({
        id: cell.id,
        row: cell.row,
        col: cell.col,
        number: cell.number,
        // color removed - derived from level
        status: cell.status,
        level: cell.level,
        corner: cell.corner,
        // Persist override flags so manual corner/number choices survive saves
        cornerOverride: !!cell.cornerOverride,
        manualCorner: cell.manualCorner === undefined ? undefined : cell.manualCorner,
        numberOverride: !!cell.numberOverride,
        manualNumber: cell.manualNumber === undefined ? undefined : cell.manualNumber,
        groupid: boothToGroup[String(cell.id)] || boothToGroup[String(cell.number)] || null
      })),
      rules, // List of enabled rules with parameters
      // Only include boothGroups if at least one rule has groups
        ...(Object.keys(boothGroups).length > 0 ? { boothGroups } : {}),
        // Event admins assigned to this layout (optional). Ensure the creator
        // is included for new layouts so they have admin access by default.
        ...(() => {
          const admins = Array.isArray(layoutEventAdmins) ? sanitizeArr(layoutEventAdmins) : [];
          try {
            if (!currentLayout && currentUser?.uid && !admins.includes(currentUser.uid)) {
              admins.unshift(currentUser.uid);
            }
          } catch (e) {}
          return admins.length > 0 ? { eventAdmins: admins } : {};
        })()
    };
    // Update in-memory gridData so groupid is available in the UI immediately
    try {
      const annotated = gridData.map(cell => ({
        ...cell,
        groupid: boothToGroup[String(cell.id)] || boothToGroup[String(cell.number)] || null
      }));
      setGridData(annotated);
    } catch (e) {
      // ignore if state update not desirable in some contexts
    }

    // Debug: show customLevels that will be persisted as part of this layout
    try { /* suppressed debug: customLevels to-save */ } catch(e) {}

    return layout;
  };

  const handleBackNavigation = async () => {
    if (hasUnsavedChanges) {
      setShowUnsavedConfirm(true);
      return;
    }
    // No unsaved changes, navigate directly
    navigate('/admin/layouts');
  };

  const saveThenNavigateBack = async () => {
    try {
      let nameToSave = layoutName.trim();
      // If no layout name, prompt for one
      if (!nameToSave) {
        const provideName = window.confirm('Layout name is required to save. Would you like to provide a name?');
        if (provideName) {
          const name = prompt('Enter layout name:');
          if (name && name.trim()) {
            nameToSave = name.trim();
            setLayoutName(nameToSave);
          } else {
            // User cancelled or provided empty name
            return;
          }
        } else {
          // User chose not to provide name
          return;
        }
      }
      // Ensure unique name by prompting user if duplicate exists
      const uniqueName = await resolveUniqueNameInteractive(nameToSave, currentLayout?.id);
      if (!uniqueName) return; // user cancelled
      if (uniqueName !== nameToSave) setLayoutName(uniqueName);

      if (!currentUser) {
        alert('You must be logged in to save layouts');
        return;
      }

      const layoutData = createLayoutData(uniqueName, layoutStatus);
      let savedLayout;
      if (currentLayout && currentLayout.id) {
        // Always replace the full layout document so additions/deletions persist.
          // full document update
        await updateLayoutInDB(currentLayout.id, layoutData, currentUser.uid);
        savedLayout = { ...layoutData, id: currentLayout.id };
        const updatedLayouts = savedLayouts.map(l => l.id === currentLayout.id ? savedLayout : l);
        setSavedLayouts(updatedLayouts);
      } else {
        const docRef = await createLayoutInDB(layoutData, currentUser.uid);
        savedLayout = { ...layoutData, id: docRef.id };
        setCurrentLayout(savedLayout);
        setSavedLayouts(prev => [...prev, savedLayout]);
      }
      setHasUnsavedChanges(false);
      navigate('/admin/layouts');
    } catch (error) {
      console.error('Save failed:', error);
      alert(`Failed to save layout: ${error.message}`);
    }
  };

  const leaveWithoutSavingIfConfirmed = () => {
    const confirmLeave = window.confirm('Are you sure you want to leave without saving your changes?');
    if (confirmLeave) {
      navigate('/admin/layouts');
    }
  };

  const saveLayout = async () => {
    if (!layoutName.trim()) {
      alert('Please enter a layout name');
      return;
    }

    if (!currentUser) {
      alert('You must be logged in to save layouts');
      return;
    }

    try {
      // Ensure unique name by prompting the user if necessary
      const uniqueName = await resolveUniqueNameInteractive(layoutName.trim(), currentLayout?.id);
      if (!uniqueName) return; // user cancelled
      if (uniqueName !== layoutName.trim()) setLayoutName(uniqueName);
      const layoutData = createLayoutData(uniqueName, layoutStatus);

      let savedLayout;

      if (currentLayout && currentLayout.id) {
        // Always replace the full layout document for existing layouts.
        try {
          // full document update
          await updateLayoutInDB(currentLayout.id, layoutData, currentUser.uid);
          savedLayout = { ...layoutData, id: currentLayout.id };

          // Update local cache
          const updatedLayouts = savedLayouts.some(l => l.id === currentLayout.id)
            ? savedLayouts.map(l => (l.id === currentLayout.id ? savedLayout : l))
            : [...savedLayouts, savedLayout];
          setSavedLayouts(updatedLayouts);

          alert(`Layout "${layoutData.name}" updated successfully!`);
          setHasUnsavedChanges(false);
        } catch (updateError) {
          // If the doc truly doesn't exist, optionally fall back to creating a new one
          const code = updateError?.code || '';
          const msg = updateError?.message || '';
          const notFound = code === 'not-found' || /No document to update/i.test(msg);
          if (!notFound) {
            console.error('Update failed:', updateError);
            alert(`Failed to update layout: ${msg}`);
            return;
          }

          const createNew = window.confirm(
            `The original layout couldn't be found. Save as a new layout instead?`
          );
          if (!createNew) return;

          const docRef = await createLayoutInDB(layoutData, currentUser.uid);
          savedLayout = { ...layoutData, id: docRef.id };
          setSavedLayouts([...savedLayouts, savedLayout]);
          alert(`Layout "${layoutData.name}" saved successfully!`);
          setHasUnsavedChanges(false);
        }
      } else {
        // No current id: create new
        const docRef = await createLayoutInDB(layoutData, currentUser.uid);
        savedLayout = { ...layoutData, id: docRef.id };
        setSavedLayouts([...savedLayouts, savedLayout]);
        alert(`Layout "${layoutData.name}" saved successfully!`);
        setHasUnsavedChanges(false);
      }

      setCurrentLayout(savedLayout);
    } catch (error) {
      console.error('Error saving layout:', error);
      alert('Failed to save layout. Please try again.');
    }
  };

  // Handler when a group is removed inside RulesAdminPanel — persist immediately
  const handleGroupRemoved = async (ruleId, removedGroup) => {
    if (!removedGroup) return;
    try {
      // Update local rulesConfig
      const updated = { ...rulesConfig };
      const groups = Array.isArray(updated[ruleId]?.groups) ? updated[ruleId].groups.filter(g => g.id !== removedGroup.id) : [];
      updated[ruleId] = { ...updated[ruleId], groups };
      setRulesConfig(updated);

      if (!currentLayout || !currentLayout.id || !currentUser) {
        // Nothing to persist to DB here
        setHasUnsavedChanges(true);
        return;
      }

      // Update boothGroups on layout doc
      const updatedBoothGroups = { ...(currentLayout.boothGroups || {}) };
      if (Array.isArray(updatedBoothGroups[ruleId])) {
        updatedBoothGroups[ruleId] = updatedBoothGroups[ruleId].filter(g => g.id !== removedGroup.id);
        if (updatedBoothGroups[ruleId].length === 0) delete updatedBoothGroups[ruleId];
      }

      // Persist meta change (boothGroups) without overwriting cellData
      await updateLayoutInDB(currentLayout.id, { boothGroups: updatedBoothGroups }, currentUser.uid);

      // Clear groupid on affected cells
      const boothIds = Array.isArray(removedGroup.booths) ? removedGroup.booths : [];
      const updates = boothIds.map(b => ({ id: b, groupid: null }));
      if (updates.length > 0) await updateLayoutCellGroupIds(currentLayout.id, updates);

      // Update local currentLayout and gridData
      setCurrentLayout(prev => prev ? { ...prev, boothGroups: updatedBoothGroups } : prev);
      setGridData(prev => prev.map(c => ({
        ...c,
        groupid: (boothIds.includes(String(c.id)) || boothIds.includes(String(c.number))) ? null : c.groupid
      })));

      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to remove group and update DB:', error);
      alert('Failed to remove group from database. See console for details.');
    }
  };

  const saveLayoutAsCopy = async () => {
    if (!layoutName.trim()) {
      alert('Please enter a layout name');
      return;
    }
    if (!currentUser) {
      alert('You must be logged in to save layouts');
      return;
    }
    try {
      // Build data and adjust name for the copy
  const baseData = createLayoutData(layoutName, layoutStatus);
  const copyNameBase = layoutName.endsWith(' (Copy)') ? `${layoutName} ${new Date().toLocaleTimeString()}` : `${layoutName} (Copy)`;
  const uniqueCopyName = await resolveUniqueNameInteractive(copyNameBase, null);
  if (!uniqueCopyName) return; // user cancelled
  const copyData = { ...baseData, name: uniqueCopyName };

      const docRef = await createLayoutInDB(copyData, currentUser.uid);
      const savedLayout = { ...copyData, id: docRef.id };
      setSavedLayouts(prev => [...prev, savedLayout]);
      setCurrentLayout(savedLayout);
  alert(`Layout saved as copy: "${uniqueCopyName}"`);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving layout copy:', error);
      alert('Failed to save layout copy. Please try again.');
    }
  };

  const loadLayout = (layout) => {
    const { 
      gridSettings, 
      cellData, 
      customLevels: layoutLevels, 
      numberingScheme: layoutNumberingScheme,
      skipEmptyRows: layoutSkipEmptyRows
    } = layout;
    
    // Update grid settings (guard in case layout is missing gridSettings)
    if (gridSettings && typeof gridSettings === 'object') {
      setRows(gridSettings.rows ?? rows);
      setColumns(gridSettings.columns ?? columns);
      setInputRows(gridSettings.rows ?? inputRows);
      setInputColumns(gridSettings.columns ?? inputColumns);
      setCellSize(gridSettings.cellSize ?? cellSize);
      setCellPadding(gridSettings.cellPadding ?? cellPadding);
      setFontSize(gridSettings.fontSize ?? fontSize);
      setLayoutMapUrl(gridSettings.mapUrl ?? layoutMapUrl);
      try { setSurroundings(gridSettings.surroundings ?? surroundings); } catch(e) {}
      setSurroundingsBgColor(gridSettings.surroundingsBgColor ?? surroundingsBgColor);
      setSurroundingsTextColor(gridSettings.surroundingsTextColor ?? surroundingsTextColor);
      setSurroundingsFontSize(gridSettings.surroundingsFontSize ?? surroundingsFontSize);
    }
    
    // Update custom levels (use layout levels only if provided)
    // Normalize to cents so the rest of the app has a consistent representation.
    if (layoutLevels && typeof layoutLevels === 'object') {
      try {
        setCustomLevels(normalizeLevelsToCents(layoutLevels));
      } catch (e) {
        console.warn('Failed to normalize customLevels from layout', e);
        setCustomLevels(layoutLevels);
      }
    }
    
    // Update numbering scheme (use layout scheme or fallback to default)
    setNumberingScheme(layoutNumberingScheme || 'alpha-numeric');
    
    // Update numbering options (use layout options or fallback to defaults)
    setSkipEmptyRows(layoutSkipEmptyRows !== undefined ? layoutSkipEmptyRows : false);
    
    // Update grid data (ensure array shape). Support both array or object map stored in older layouts.
    let safeCellData = [];
    if (Array.isArray(cellData)) safeCellData = cellData;
    else if (cellData && typeof cellData === 'object') safeCellData = Object.values(cellData);

    // Normalize and apply saved override flags so UI honors manual overrides
    const normalized = safeCellData.map(c => {
      const cell = { ...c };
      // Ensure boolean flags exist
      cell.cornerOverride = !!cell.cornerOverride;
      cell.numberOverride = !!cell.numberOverride;
      // Preserve manual values if present, but if cornerOverride is true prefer manualCorner
      if (cell.cornerOverride && (cell.manualCorner === 'Y' || cell.manualCorner === 'N')) {
        cell.corner = cell.manualCorner;
      }
      // Ensure sensible defaults for missing fields so UI doesn't show Unknown
      if (!cell.status) cell.status = 'Available';
      if (!cell.level) cell.level = 'Basic';
      if (typeof cell.corner === 'undefined' || cell.corner === null) cell.corner = 'N';
      // If number override present and manualNumber exists, ensure manualNumber is kept
      if (cell.numberOverride && typeof cell.manualNumber !== 'undefined') {
        // number field itself may be left in place; manualNumber stored separately
      }
      return cell;
    });

    setGridData(normalized);
    setCurrentLayout(layout);
    setLayoutName(layout.name);
    setLayoutStatus(layout.status);
    setHasUnsavedChanges(false); // Reset when loading a layout
    // Load eventAdmins into local selector state
    try {
      setLayoutEventAdmins(Array.isArray(layout.eventAdmins) ? layout.eventAdmins : []);
    } catch (e) {}
    
    // No alert when loading from navigation
    setShowLayoutManager(false);

    // Also load rules configuration from the layout document
    try {
      const cfg = buildRulesConfigFromLayout(layout, appDefaults);
      setRulesConfig(cfg);
    } catch (e) {
      console.warn('Failed to load rules config from layout:', e?.message || e);
    }
  };

  const deleteLayout = async (layoutId) => {
    if (window.confirm('Are you sure you want to delete this layout?')) {
      try {
        await deleteLayoutFromDB(layoutId, currentUser.uid);
        const updatedLayouts = savedLayouts.filter(layout => layout.id !== layoutId);
        setSavedLayouts(updatedLayouts);
        
        if (currentLayout && currentLayout.id === layoutId) {
          setCurrentLayout(null);
        }
      } catch (error) {
        console.error('Error deleting layout:', error);
        alert('Failed to delete layout. Please try again.');
      }
    }
  };

  const updateLayoutStatus = async (layoutId, newStatus) => {
    try {
      await updateLayoutStatusInDB(layoutId, newStatus, currentUser.uid);
      
      const updatedLayouts = savedLayouts.map(layout => 
        layout.id === layoutId 
          ? { ...layout, status: newStatus, updatedAt: new Date().toISOString() }
          : layout
      );
      setSavedLayouts(updatedLayouts);
      
      if (currentLayout && currentLayout.id === layoutId) {
        setCurrentLayout({ ...currentLayout, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating layout status:', error);
      alert('Failed to update layout status. Please try again.');
    }
  };

  // Load saved layouts from Firestore on component mount
  React.useEffect(() => {
    const loadLayouts = async () => {
      if (!currentUser) return;

      try {
        const layouts = await getUserLayouts(currentUser.uid);
        setSavedLayouts(layouts);
      } catch (error) {
        console.error('Error loading layouts:', error);
      }
    };

    loadLayouts();
  }, [currentUser]);

  // Handle navigation state (new layout or existing layout)
  React.useEffect(() => {
    if (location.state) {
      if (location.state.newLayoutName) {
      // Starting with a new layout with suggested name
      setLayoutName(location.state.newLayoutName);
      setCurrentLayout(null);
      setLayoutEventAdmins(currentUser?.uid ? [currentUser.uid] : []);
      setHasUnsavedChanges(false); // Reset for new layout
        // Sync input values with current grid dimensions
        setInputRows(rows);
        setInputColumns(columns);
      } else if (location.state.existingLayout) {
        // Loading an existing layout
        const layout = location.state.existingLayout;
        
        loadLayout(layout);
      }
    }
  }, [location.state]);

  

  // Load EventAdmin profiles for selector
  useEffect(() => {
    (async () => {
      try {
        const admins = await listProfilesByType('EventAdmin');
        setProfiles(admins || []);
      } catch (e) {
        console.warn('Failed to load EventAdmin profiles for layout selector:', e);
        setProfiles([]);
      }
    })();
  }, []);

  const toggleInvisible = () => {
    if (isLocked) return;
    if (selectedCells.size === 0) return;

    const updatedGridData = gridData.map(cell => {
      if (selectedCells.has(cell.id)) {
        const cellIsInvisible = cell.status === 'Invisible' && cell.level === 'Invisible';
        
        if (cellIsInvisible) {
          // Restore from invisible - set defaults as per checkbox logic
          return {
            ...cell,
            status: 'Available',
            level: 'Basic',
            number: '000',
            // color derived from level
            corner: 'N'
          };
        } else {
          // Make invisible - set invisible properties as per checkbox logic
          return {
            ...cell,
            status: 'Invisible',
            level: 'Invisible',
            corner: 'N',
            number: null
            // color derived from level
          };
        }
      }
      return cell;
    });
    
    // Update corner flags after visibility changes
    const finalGridData = updateCornerFlags(updatedGridData);
    setGridData(finalGridData);
    setSelectedCells(new Set());
  };

  // Booth selection features moved to BoothSelection page

  const regenerateGrid = () => {
    if (isLocked) return;
    // Show confirmation dialog
    const resetOptions = [];
    if (statusReset) resetOptions.push('Status');
    if (levelReset) resetOptions.push('Level');
    
    const resetText = resetOptions.length > 0 
      ? `\n\nThe following will be reset: ${resetOptions.join(', ')}`
      : '\n\nCell status and level will be preserved.';
    
    const confirmRegenerate = window.confirm(
      `Are you sure you want to regenerate the grid with ${inputRows} rows and ${inputColumns} columns?${resetText}\n\nThis will recreate the grid structure and renumber all cells.\n\nThis action cannot be undone.`
    );
    
    if (!confirmRegenerate) {
      return;
    }

    // Create a map of existing cell data by position for preservation
    const existingCellMap = new Map();
    gridData.forEach(cell => {
      existingCellMap.set(`${cell.row}-${cell.col}`, cell);
    });

    // Update the actual rows and columns to match inputs
    setRows(inputRows);
    setColumns(inputColumns);
    
    const newGridData = [];
    
    // First, generate all cells with basic data
    for (let row = 1; row <= inputRows; row++) {
      for (let col = 1; col <= inputColumns; col++) {
        const cellKey = `${row}-${col}`;
        const existingCell = existingCellMap.get(cellKey);
        const cellData = generateCellData(row, col, inputColumns);
        
        // Preserve existing data based on reset flags
        if (existingCell) {
          // Always preserve position but use new ID format
          cellData.row = existingCell.row;
          cellData.col = existingCell.col;
          // cellData.id already uses new format from generateCellData
          
          // Conditionally preserve status
          if (!statusReset) {
            cellData.status = existingCell.status;
          }
          
          // Conditionally preserve level (color derived from level)
          if (!levelReset) {
            cellData.level = existingCell.level;
          }
        }
        
        // No automatic invisibility rules - let user control via status reset
        
        newGridData.push(cellData);
      }
    }
    
    // Then regenerate numbers using current numbering scheme
    const numberedGridData = applyBoothNumbering(newGridData, numberingScheme, skipEmptyRows, inputRows, inputColumns);
    
    // Finally, update corner flags based on invisible neighbors
    const finalGridData = updateCornerFlags(numberedGridData);
    
    setGridData(finalGridData);
    setSelectedCell(null);
    setSelectedCells(new Set());
    setHasUnsavedChanges(true); // Mark as changed when grid is regenerated
  };

  const regenerateNumbers = () => {
    if (isLocked) return;
    // Show numbering scheme popup
    setShowNumberingScheme(true);
  };

  const reapplyNumbers = (grid = null) => {
    // Re-generate numbers using current scheme and options and update corners
    const source = grid || gridData;
    const updated = applyBoothNumbering(source, numberingScheme, skipEmptyRows, rows, columns);
    const finalGrid = updateCornerFlags(updated);
    setGridData(finalGrid);
    setHasUnsavedChanges(true);
  };

  // Custom number handled inside PropertyPopup for single-cell edits

  // Custom number handled inside PropertyPopup for single-cell edits

  // Custom number handled inside PropertyPopup for single-cell edits

  const handleApplyNumberingScheme = (scheme, skipRows) => {
    // Update the numbering scheme and options
    setNumberingScheme(scheme);
    setSkipEmptyRows(skipRows);
    
    // Apply numbering with the selected scheme and options
    const updatedGridData = applyBoothNumbering(gridData, scheme, skipRows, rows, columns);
    
    // Update corner flags after number regeneration
    const finalGridData = updateCornerFlags(updatedGridData);
    
    setGridData(finalGridData);
    setSelectedCells(new Set());
    setHasUnsavedChanges(true);
  };

  // Function to detect and mark corner cells
  const updateCornerFlags = (gridData) => {
    return gridData.map(cell => {
      if (cell.status === 'Invisible') {
        return { ...cell, corner: 'N' }; // Invisible cells are not corners
      }

      // If cell has manual corner override, don't recalculate - keep the manual value
      if (cell.cornerOverride) {
        return { ...cell, corner: cell.manualCorner || cell.corner };
      }

      const { row, col } = cell;
      let invisibleNeighborCount = 0;

      // Check only 4 cardinal directions (no diagonals)
      const neighbors = [
        { r: row - 1, c: col },     // Top
        { r: row, c: col - 1 },     // Left
        { r: row, c: col + 1 },     // Right
        { r: row + 1, c: col }      // Bottom
      ];

      for (const neighbor of neighbors) {
        // Skip neighbors that are outside grid bounds (don't count as invisible)
        if (neighbor.r < 1 || neighbor.r > rows || neighbor.c < 1 || neighbor.c > columns) {
          continue; // Grid edges are not considered invisible
        }

        // Check if neighbor cell is invisible
        const neighborCell = gridData.find(c => c.row === neighbor.r && c.col === neighbor.c);
        if (neighborCell && neighborCell.status === 'Invisible') {
          invisibleNeighborCount++;
        }
      }

      // Cell is corner if it has at least 2 invisible neighbors in cardinal directions
      return { ...cell, corner: invisibleNeighborCount >= 2 ? 'Y' : 'N' };
    });
  };

  return (
    <div className="profile-container profile-wide-container grid-builder-container">
      <div className="grid-builder-header">
        <div className="header-content">
          <div className="title-section">
            <h1>Layout Builder</h1>
            <p>Create and customize your layout with interactive cells</p>
          </div>
          
          <div className="header-actions">
            <div className="layout-name-block">
              <input
                type="text"
                placeholder="Layout name..."
                value={layoutName}
                onChange={(e) => {
                  setLayoutName(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                className="header-layout-name"
                disabled={isLocked}
              />
              <div className="layout-id-hint">
                {currentLayout?.id ? `ID: ${currentLayout.id}` : 'ID: (unsaved)'}
              </div>
            </div>
            <select 
              value={layoutStatus} 
              onChange={(e) => {
                setLayoutStatus(e.target.value);
                setHasUnsavedChanges(true);
              }}
              className="header-layout-status"
            >
              <option value="Draft">Draft</option>
              <option value="Published">Published</option>
              <option value="Inactive">Inactive</option>
            </select>
            {/* EventAdmins selector moved below into Grid Structure area */}
            <button 
              onClick={saveLayout}
              className="quick-save-btn"
              disabled={!layoutName.trim()}
            >
              💾 Save
            </button>
            <button 
              onClick={handleBackNavigation} 
              className="back-btn"
              title="Back to Layouts"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {isLocked && (
        <div className="profile-hint" style={{ margin: '12px 16px', color: '#8a6d3b', background: '#fff8e1', border: '1px solid #ffecb3', padding: '8px 12px', borderRadius: 6 }}>
          This layout is <strong>{layoutStatus}</strong>. Change status to Draft to make edits.
        </div>
      )}

      <div className="grid-controls-compact">
        {/* Top-right card: EventAdmins */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <div style={{ width: 380, border: '1px solid #e6e9ec', background: '#fff', padding: 12, borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Event Admins</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <select
                value={selectedAdmin}
                onChange={e => setSelectedAdmin(e.target.value)}
                className="profile-input"
                style={{ flex: 1, minWidth: 180 }}
              >
                <option value="">-- Select Event Admin --</option>
                {profiles.filter(p => p.userType === 'EventAdmin').map(p => (
                  <option key={p.id} value={p.id}>{((p.firstName||'') + ' ' + (p.lastName||'')).trim() || p.email || p.id}</option>
                ))}
              </select>
              <button
                type="button"
                className="apply-btn"
                onClick={() => {
                  if (!selectedAdmin) return;
                  setLayoutEventAdmins(prev => {
                    const list = Array.isArray(prev) ? [...prev] : [];
                    if (!list.includes(selectedAdmin)) list.push(selectedAdmin);
                    return list;
                  });
                  setSelectedAdmin('');
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                Add
              </button>
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto' }}>
              {Array.isArray(layoutEventAdmins) && layoutEventAdmins.length > 0 ? (
                layoutEventAdmins.map(uid => {
                  const p = profiles.find(x => x.id === uid);
                  const name = p ? ((p.firstName||'') + ' ' + (p.lastName||'')).trim() || p.email || uid : uid;
                  return (
                    <div key={uid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f3f5' }}>
                      <div style={{ fontSize: 13, color: '#333' }}>{name}</div>
                      <button type="button" className="cancel-btn" onClick={() => setLayoutEventAdmins(prev => (prev || []).filter(x => x !== uid))}>Remove</button>
                    </div>
                  );
                })
              ) : (
                <div style={{ fontSize: 13, color: '#666' }}>No Event Admins assigned</div>
              )}
            </div>
          </div>
        </div>
        {/* Grid Structure Group */}
        <div className="control-section">
          <h4 className="section-title">Grid Structure</h4>
          <div className="control-row">
            <div className="control-row-top" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="control-group compact" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <label htmlFor="rows" style={{ display: 'block', textAlign: 'left', lineHeight: '20px' }}>Rows:</label>
                <input
                  type="number"
                  id="rows"
                  value={inputRows}
                  onChange={(e) => setInputRows(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  min="1"
                  max="50"
                  disabled={isLocked}
                  style={{ textAlign: 'left', height: 36, boxSizing: 'border-box' }}
                />
              </div>

              <div className="control-group compact" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <label htmlFor="columns" style={{ display: 'block', textAlign: 'left', lineHeight: '20px' }}>Columns:</label>
                <input
                  type="number"
                  id="columns" 
                  value={inputColumns}
                  onChange={(e) => setInputColumns(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  min="1"
                  max="50"
                  disabled={isLocked}
                  style={{ textAlign: 'left', height: 36, boxSizing: 'border-box' }}
                />
              </div>

              <div className="regenerate-options compact" style={{ display: 'flex', gap: 8, alignItems: 'center', height: 36 }}>
                <div className="reset-checkboxes compact" style={{ display: 'flex', gap: 12, alignItems: 'center', height: 36 }}>
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36 }}>
                    <input
                      type="checkbox"
                      checked={statusReset}
                      onChange={(e) => { setStatusReset(e.target.checked); setHasUnsavedChanges(true); }}
                      style={{ transform: 'scale(1.05)', margin: 0 }}
                    />
                    <span style={{ textAlign: 'left', lineHeight: '36px' }}>Reset Status</span>
                  </label>
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36 }}>
                    <input
                      type="checkbox"
                      checked={levelReset}
                      onChange={(e) => { setLevelReset(e.target.checked); setHasUnsavedChanges(true); }}
                      style={{ transform: 'scale(1.05)', margin: 0 }}
                    />
                    <span style={{ textAlign: 'left', lineHeight: '36px' }}>Reset Level</span>
                  </label>
                </div>
              </div>

              <div className="control-group compact" style={{ flex: 2, minWidth: 360, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <label htmlFor="layoutMapUrl" style={{ display: 'block', textAlign: 'left', lineHeight: '20px' }}>Layout Map URL:</label>
                <input
                  type="text"
                  id="layoutMapUrl"
                  placeholder="https://..."
                  value={layoutMapUrl}
                  onChange={(e) => { setLayoutMapUrl(e.target.value); setHasUnsavedChanges(true); }}
                  disabled={isLocked}
                  style={{ width: '100%', textAlign: 'left', height: 36, boxSizing: 'border-box' }}
                />
              </div>

              {/* Surroundings inputs (inline within Grid Structure) */}
              <div className="surroundings-row" style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                <div className="control-group compact" style={{ minWidth: 220, flex: '1 1 220px' }}>
                  <label style={{ display: 'block', lineHeight: '20px' }}>Top:</label>
                  <input
                    type="text"
                    value={(surroundings.top || []).join(', ')}
                    onChange={(e) => { setSurroundings(prev => ({ ...prev, top: e.target.value.split(',') })); setHasUnsavedChanges(true); }}
                    placeholder=""
                    style={{ width: '100%', height: 44, fontSize: 15, padding: '8px 10px', boxSizing: 'border-box', alignItems: 'center' }}
                  />
                </div>

                <div className="control-group compact" style={{ minWidth: 220, flex: '1 1 220px' }}>
                  <label style={{ display: 'block', lineHeight: '20px' }}>Right:</label>
                  <input
                    type="text"
                    value={(surroundings.right || []).join(', ')}
                    onChange={(e) => { setSurroundings(prev => ({ ...prev, right: e.target.value.split(',') })); setHasUnsavedChanges(true); }}
                    placeholder=""
                    style={{ width: '100%', height: 44, fontSize: 15, padding: '8px 10px', boxSizing: 'border-box' }}
                  />
                </div>

                <div className="control-group compact" style={{ minWidth: 220, flex: '1 1 220px' }}>
                  <label style={{ display: 'block', lineHeight: '20px' }}>Bottom:</label>
                  <input
                    type="text"
                    value={(surroundings.bottom || []).join(', ')}
                    onChange={(e) => { setSurroundings(prev => ({ ...prev, bottom: e.target.value.split(',') })); setHasUnsavedChanges(true); }}
                    placeholder=""
                    style={{ width: '100%', height: 44, fontSize: 15, padding: '8px 10px', boxSizing: 'border-box' }}
                  />
                </div>

                <div className="control-group compact" style={{ minWidth: 220, flex: '1 1 220px' }}>
                  <label style={{ display: 'block', lineHeight: '20px' }}>Left:</label>
                  <input
                    type="text"
                    value={(surroundings.left || []).join(', ')}
                    onChange={(e) => { setSurroundings(prev => ({ ...prev, left: e.target.value.split(',') })); setHasUnsavedChanges(true); }}
                    placeholder=""
                    style={{ width: '100%', height: 44, fontSize: 15, padding: '8px 10px', boxSizing: 'border-box' }}
                  />
                </div>
                {/* Background color, text color and font size for surrounding labels */}
                <div className="control-group compact" style={{ minWidth: 260, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <label style={{ display: 'block', lineHeight: '20px' }}>Label background:</label>
                    <input
                      type="color"
                      value={surroundingsBgColor}
                      onChange={(e) => { setSurroundingsBgColor(e.target.value); setHasUnsavedChanges(true); }}
                      title="Pick background color for surrounding labels"
                      style={{ width: 48, height: 34, padding: 0, border: 'none', background: 'transparent' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <label style={{ display: 'block', lineHeight: '20px' }}>Text color:</label>
                    <input
                      type="color"
                      value={surroundingsTextColor}
                      onChange={(e) => { setSurroundingsTextColor(e.target.value); setHasUnsavedChanges(true); }}
                      title="Pick text color for surrounding labels"
                      style={{ width: 48, height: 34, padding: 0, border: 'none', background: 'transparent' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <label style={{ display: 'block', lineHeight: '20px' }}>Font size:</label>
                    <input
                      type="number"
                      min="10"
                      max="24"
                      value={surroundingsFontSize}
                      onChange={(e) => { setSurroundingsFontSize(Math.max(10, Math.min(24, parseInt(e.target.value || 14)))); setHasUnsavedChanges(true); }}
                      style={{ width: 64, height: 34, padding: '4px 8px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              </div>

            </div>

            <div className="control-row-bottom" style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center', justifyContent: 'center' }}>
              <button onClick={regenerateGrid} className="regenerate-btn compact" disabled={isLocked} style={{ height: 36 }}>
                🔄 Regenerate Grid
              </button>

              <button onClick={regenerateNumbers} className="regenerate-numbers-btn compact" disabled={isLocked} style={{ height: 36 }}>
                🔢 Regenerate Numbers
              </button>
              {/* Custom number moved into Edit Properties popup */}
            </div>
          </div>
        </div>

        

        {/* Visual Settings Group */}
        <div className="control-section">
          <h4 className="section-title">Visual Settings</h4>
          <div className="control-row">
            <div className="control-group compact">
              <label htmlFor="cellSize">Cell Size:</label>
              <input
                type="range"
                id="cellSize"
                value={cellSize}
                onChange={(e) => setCellSize(parseInt(e.target.value))}
                min="30"
                max="150"
                disabled={isLocked}
              />
              <span className="range-value">{cellSize}px</span>
            </div>

            <div className="control-group compact">
              <label htmlFor="cellPadding">Spacing:</label>
              <input
                type="range"
                id="cellPadding"
                value={cellPadding}
                onChange={(e) => setCellPadding(parseInt(e.target.value))}
                min="2"
                max="20"
                disabled={isLocked}
              />
              <span className="range-value">{cellPadding}px</span>
            </div>

            <div className="control-group compact">
              <label htmlFor="fontSize">Font Size:</label>
              <input
                type="range"
                id="fontSize"
                value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
                min="8"
                max="48"
                disabled={isLocked}
              />
              <span className="range-value">{fontSize}px</span>
            </div>
          </div>
        </div>

        {/* Mode & Tools Group */}
        <div className="control-section">
          <h4 className="section-title">Tools & Configuration</h4>
          <div className="control-row">
            <button 
              onClick={toggleMultiSelectMode} 
              className={`multi-select-btn compact ${isMultiSelectMode ? 'active' : ''}`}
              disabled={isLocked}
            >
              {isMultiSelectMode ? '📋 Exit Multi-Select' : '📋 Multi-Select'}
            </button>

            <button 
              onClick={() => setShowLevelManager(true)} 
              className="level-manager-btn compact"
              title="Manage level definitions, colors, and pricing"
              disabled={isLocked}
            >
              🎨 Level Manager
            </button>

            <button 
              onClick={() => setShowRulesAdmin(true)} 
              className="rules-admin-btn compact"
              title="Configure booth selection rules"
            >
              ⚙️ Rules Config
            </button>

            {/** Layout Manager button removed per request; duplication handled in Layout Admin page */}

            {/* Open Booth Selection page with current layout snapshot */}
            <button
              onClick={() => {
                const name = layoutName && layoutName.trim() ? layoutName : `Layout_${Date.now()}`;
                const snapshot = createLayoutData(name, layoutStatus);
                navigate('/booth-selection', { state: { existingLayout: { ...snapshot, id: currentLayout?.id }, preview: true, backTo: '/layout-builder' } });
              }}
              className="booth-mode-btn compact"
              title="Open Booth Selection page"
            >
              🏪 Open Booth Selection
            </button>
          </div>
        </div>
      </div>


      {isMultiSelectMode && (
        <div className="multi-select-controls">
          <div className="selection-info">
            <span>
              {selectedCells.size} cells selected
              {selectedRows.size > 0 && ` • ${selectedRows.size} rows selected`}
              {selectedColumns.size > 0 && ` • ${selectedColumns.size} columns selected`}
            </span>
            <small>Click cells to select • Hold Ctrl/Cmd + click row/column buttons for multi-selection • Invisible cells (dashed border) are clickable</small>
          </div>
          <div className="selection-actions">
            <button onClick={selectAll} className="select-all-btn" disabled={isLocked}>
              Select All
            </button>
            <button onClick={clearSelection} className="clear-selection-btn" disabled={isLocked}>
              Clear Selection
            </button>
            <button 
              onClick={toggleInvisible} 
              className="toggle-invisible-btn"
              disabled={selectedCells.size === 0 || isLocked}
            >
              Toggle Invisible ({selectedCells.size})
            </button>
            <button 
              onClick={openBulkEditor} 
              className="bulk-edit-btn"
              disabled={selectedCells.size === 0 || isLocked}
            >
              Edit Properties ({selectedCells.size})
            </button>
            {/* Custom number moved into Edit Properties popup */}
          </div>
        </div>
      )}

      <div className="grid-container">
        <div className="grid-scroll-wrapper">
        {isMultiSelectMode && (
          <div className="grid-with-selectors">
            {/* Column selectors */}
            <div className="column-selectors">
              <div className="selector-spacer"></div>
              {Array.from({length: columns}, (_, index) => (
                <button
                  key={`col-${index + 1}`}
                  className={`column-selector ${selectedColumns.has(index + 1) ? 'selected' : ''}`}
                  onClick={(e) => selectColumn(index + 1, e)}
                  style={{
                    width: `${cellSize}px`,
                    height: '30px'
                  }}
                  title={`Select column ${index + 1}`}
                >
                  C{index + 1}
                </button>
              ))}
            </div>
            
            <div className="grid-with-row-selectors">
              {/* Row selectors */}
              <div className="row-selectors">
                {Array.from({length: rows}, (_, index) => (
                  <button
                    key={`row-${index + 1}`}
                    className={`row-selector ${selectedRows.has(index + 1) ? 'selected' : ''}`}
                    onClick={(e) => selectRow(index + 1, e)}
                    style={{
                      width: '30px',
                      height: `${cellSize}px`
                    }}
                    title={`Select row ${index + 1}`}
                  >
                    R{index + 1}
                  </button>
                ))}
              </div>
              
              {/* Main grid */}
              <div style={{ position: 'relative' }}>
                <div 
                  className="dynamic-grid"
                  style={{
                    gridTemplateColumns: `repeat(${columns}, ${cellSize}px)`,
                    gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
                    gap: `${cellPadding}px`,
                  }}
                >
                  {gridData.map((cellData) => (
                    <GridCell
                      key={cellData.id}
                      cellData={cellData}
                      onClick={handleCellClick}
                      isSelected={isMultiSelectMode ? selectedCells.has(cellData.id) : false}
                      isRequired={false}
                      isBoothMode={false}
                      cellSize={cellSize}
                      fontSize={fontSize}
                      isMultiSelect={isMultiSelectMode}
                      customLevels={customLevels}
                      rulesConfig={rulesConfig}
                    />
                  ))}
                </div>

                {/* Group boundaries overlay (absolute so they don't affect grid layout) */}
                {/* Group overlays disabled */}
              </div>
            </div>
          </div>
        )}
        
        {!isMultiSelectMode && (
          <div style={{ position: 'relative' }}>
            <div 
              className="dynamic-grid"
              style={{
                gridTemplateColumns: `repeat(${columns}, ${cellSize}px)`,
                gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
                gap: `${cellPadding}px`,
                justifyContent: 'center'
              }}
            >
              {gridData.map((cellData) => (
                <GridCell
                  key={cellData.id}
                  cellData={cellData}
                  onClick={handleCellClick}
                  isSelected={false}
                  isRequired={false}
                  isBoothMode={false}
                  cellSize={cellSize}
                  fontSize={fontSize}
                  isMultiSelect={false}
                  customLevels={customLevels}
                  rulesConfig={rulesConfig}
                />
              ))}
            </div>

            {/* Group overlays disabled */}
          </div>
        )}
        </div>
      </div>

      <PropertyPopup 
        selectedCell={selectedCell} 
        onClose={closePopup} 
        show={showPopup} 
        onApplyChanges={applySingleCellChanges}
        customLevels={customLevels}
        rulesConfig={rulesConfig}
      />

      {/* Custom number popup removed; custom number is edited via PropertyPopup */}

      <BulkPropertyEditor
        selectedCells={selectedCells}
        gridData={gridData}
        onClose={closeBulkEditor}
        show={showBulkEditor}
        onApplyChanges={applyBulkChanges}
        customLevels={customLevels}
      />

      {/* Level Manager */}
      <LevelManager
        show={showLevelManager}
        onClose={() => setShowLevelManager(false)}
        customLevels={customLevels}
        onLevelsUpdate={handleLevelsUpdate}
        currentLayoutId={currentLayout?.id}
        currentUserId={currentUser?.uid}
      />

      {/* Numbering Scheme Popup */}
      <NumberingSchemePopup
        show={showNumberingScheme}
        onClose={() => setShowNumberingScheme(false)}
        onApply={handleApplyNumberingScheme}
        currentScheme={numberingScheme}
        currentSkipEmptyRows={skipEmptyRows}
      />

      {/* Booth Rules and Shopping Cart moved to BoothSelection */}

      {/** Layout Manager render removed; not used here */}

      {/* Rules Administration Panel */}
      {showRulesAdmin && (
        <div className="admin-panel-overlay" onClick={async () => {
          // Close if no unsaved changes, otherwise prompt to save
          if (!hasUnsavedChanges) {
            setShowRulesAdmin(false);
            return;
          }
          const shouldSave = window.confirm('You have unsaved changes. Save before closing?');
          if (shouldSave) {
            // Save changes, then close
            if (!layoutName.trim()) {
              alert('Please enter a layout name');
              return;
            }
            if (!currentUser) {
              alert('You must be logged in to save layouts');
              return;
            }
            try {
              const uniqueName = await resolveUniqueNameInteractive(layoutName.trim(), currentLayout?.id);
              if (!uniqueName) return; // user cancelled
              if (uniqueName !== layoutName.trim()) setLayoutName(uniqueName);
              const layoutData = createLayoutData(uniqueName, layoutStatus);
              if (currentLayout && currentLayout.id) {
                await updateLayoutInDB(currentLayout.id, layoutData, currentUser.uid);
                setCurrentLayout({ ...layoutData, id: currentLayout.id });
              } else {
                const docRef = await createLayoutInDB(layoutData, currentUser.uid);
                setCurrentLayout({ ...layoutData, id: docRef.id });
              }
              setHasUnsavedChanges(false);
            } catch (error) {
              console.error('Error saving rule changes:', error);
              alert('Failed to save rule changes. Please try again.');
            }
          }
          setShowRulesAdmin(false);
        }}>
          <div className="admin-panel-container" onClick={e => e.stopPropagation()}>
            <RulesAdminPanel
              currentRules={rulesConfig}
              onRulesUpdate={(updated) => {
                setRulesConfig(updated);
                setHasUnsavedChanges(true);
              }}
              gridData={gridData}
              onRemoveGroup={handleGroupRemoved}
              onSaveRequest={async () => {
                // Save without closing
                if (!layoutName.trim()) {
                  alert('Please enter a layout name');
                  return;
                }
                if (!currentUser) {
                  alert('You must be logged in to save layouts');
                  return;
                }
                try {
                  const uniqueName = await resolveUniqueNameInteractive(layoutName.trim(), currentLayout?.id);
                  if (!uniqueName) return; // user cancelled
                  if (uniqueName !== layoutName.trim()) setLayoutName(uniqueName);
                  const layoutData = createLayoutData(uniqueName, layoutStatus);
                    if (currentLayout && currentLayout.id) {
                      // Always replace full document for rules admin saves as well
                      // full document update (RulesAdmin save)
                      await updateLayoutInDB(currentLayout.id, layoutData, currentUser.uid);
                      setCurrentLayout({ ...layoutData, id: currentLayout.id });
                  } else {
                    const docRef = await createLayoutInDB(layoutData, currentUser.uid);
                    setCurrentLayout({ ...layoutData, id: docRef.id });
                  }
                  // Optionally indicate saved
                  alert('Rules saved successfully.');
                  setHasUnsavedChanges(false);
                } catch (error) {
                  console.error('Error saving rule changes:', error);
                  alert('Failed to save rule changes. Please try again.');
                }
              }}
              onCloseRequest={async () => {
                // Only prompt to save if there are unsaved changes
                if (hasUnsavedChanges) {
                  const shouldSave = window.confirm('You have unsaved changes. Save before closing?');
                  if (shouldSave) {
                    if (!layoutName.trim()) {
                      alert('Please enter a layout name');
                      return;
                    }
                    if (!currentUser) {
                      alert('You must be logged in to save layouts');
                      return;
                    }
                    try {
                      const uniqueName = await resolveUniqueNameInteractive(layoutName.trim(), currentLayout?.id);
                      if (!uniqueName) return; // user cancelled
                      if (uniqueName !== layoutName.trim()) setLayoutName(uniqueName);
                      const layoutData = createLayoutData(uniqueName, layoutStatus);
                      if (currentLayout && currentLayout.id) {
                        await updateLayoutInDB(currentLayout.id, layoutData, currentUser.uid);
                        setCurrentLayout({ ...layoutData, id: currentLayout.id });
                      } else {
                        const docRef = await createLayoutInDB(layoutData, currentUser.uid);
                        setCurrentLayout({ ...layoutData, id: docRef.id });
                      }
                      setHasUnsavedChanges(false);
                    } catch (error) {
                      console.error('Error saving rule changes:', error);
                      alert('Failed to save rule changes. Please try again.');
                    }
                  }
                }
                setShowRulesAdmin(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Unsaved changes Yes/No dialog */}
      {showUnsavedConfirm && (
        <div className="popup-overlay">
          <div className="popup-content compact-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header compact-header">
              <h4>Unsaved Changes</h4>
              <button className="close-btn" onClick={() => setShowUnsavedConfirm(false)}>×</button>
            </div>
            <div className="popup-body compact">
              <p>You have unsaved changes. Would you like to save before leaving?</p>
              <div className="edit-actions compact-actions">
                <button className="apply-btn compact-btn" onClick={async () => { setShowUnsavedConfirm(false); await saveThenNavigateBack(); }}>Yes</button>
                <button className="cancel-btn compact-btn" onClick={() => { setShowUnsavedConfirm(false); leaveWithoutSavingIfConfirmed(); }}>No</button>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

const WrappedLayoutBuilder = (props) => (
  <ErrorBoundary>
    <LayoutBuilder {...props} />
  </ErrorBoundary>
);

export default WrappedLayoutBuilder;